import { useLocale as useLocaleR42 } from '@/lib/locale';
import { useLocale as useLocaleR40 } from '@/lib/locale';
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useParams, useSearchParams, Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import confetti from 'canvas-confetti';
import api, { setAuth, clearAuth, fmtPrice, fmtTime12hLong } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PaymentProviderBadge } from '@/components/PaymentProviderBadge';
import PaymentStep from '@/components/PaymentStep';
import ApplicationFeeStep from '@/components/ApplicationFeeStep';
import PasswordInput from '@/components/PasswordInput';
import Seo from '@/components/Seo';
import DateOfBirthInput from '@/components/DateOfBirthInput';
import { CoachIntroHero, CoachIntroPill } from '@/components/CoachIntroHero';
import { WaiverAgreement } from '@/components/WaiverAgreement';
import { Logo } from '@/components/Logo';
import {
    Check, ArrowLeft, ArrowRight, ShieldCheck, Plus, Trash2, ExternalLink, Sparkles, MailCheck, LogIn, CalendarDays, HandCoins
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth.jsx';

const STEPS = [
    'Account', 'Guardian', 'Students', 'Package', 'Schedule', 'Waiver', 'Payment', 'Confirmation'
];

// === Phase A2P (Twilio 10DLC compliance) constants ===
// These strings must exactly match what the backend `sms_consents` audit
// row will store — the Twilio auditor cross-checks the disclosure copy
// shown to the user against the version they claim to have accepted.

// The **short, unambiguous checkbox label**. Twilio audits reject long
// paragraph-style labels; the label must be a single-line opt-in
// affirmation. The compliance detail lives in a separate disclosure block
// rendered directly below the phone field.
const SMS_CONSENT_LABEL_PARENT =
    "I agree to receive automated text messages from Splash Pad for lesson updates, reminders, and coach communications.";

// The **compliance disclosure** rendered directly below the phone input.
// Verbatim Twilio A2P audit language — do not paraphrase. Duplicated
// (with minor tense tweaks) from Signup.jsx so both audit paths use the
// same wording.
const SMS_DISCLOSURE_TEXT_PARENT =
    "By providing your phone number and opting in below, you agree to receive recurring automated marketing and lesson-related text messages from Splash Pad. Consent is not a condition of any purchase. Msg and data rates may apply. Msg frequency varies. Reply HELP for help or STOP to cancel.";

// SMS opt-in disclosure copy that we PERSIST to the audit trail — this is
// what actually gets stored in the parent_profile.sms_opt_in_text field
// and the sms_consents.consent_language field. Includes the phone
// number so a Twilio auditor can prove which number the parent
// consented to be reached at.
const buildSmsOptInDisclosure = (phone) =>
    `${SMS_CONSENT_LABEL_PARENT} By providing ${phone || 'the phone number above'}, you agree to receive recurring automated marketing and lesson-related text messages from Splash Pad. Consent is not a condition of any purchase. Msg frequency varies and can be daily during active lesson seasons. Msg and data rates may apply. Reply HELP for help or STOP to cancel.`;

// Loose but effective client-side phone validation: reject anything with
// fewer than 10 digits after stripping formatting. The backend re-checks
// this so a mutated client can't sneak past the Twilio compliance gate.
const isValidPhone = (raw) => (String(raw || '').match(/\d/g) || []).length >= 10;

function emptyStudent() {
    // _uid is a stable client-side key so React doesn't get confused when
    // parents add/remove students mid-flow. It's stripped before submit.
    const uid = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `s-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    // _from_existing_id (client-only) is set when the parent taps an
    // existing kid from the "Your children" picker. When set, the card
    // renders in a read-only "from your account" state (name/DOB locked)
    // but medical notes + swim level stay editable so the parent can
    // update per-coach details. Backend dedups by (first, last, dob) so
    // this ID is never sent — it's purely a UI marker.
    return { _uid: uid, _from_existing_id: null, first_name: '', last_name: '', dob: '', gender: '', swim_level: '', medical_notes: '', emergency_med_authorized: false };
}

// Phase 77a — Convert an existing kid row (from GET /parent/students)
// into a `students[]` entry, resetting per-application flags that must
// re-prompt on every coach application (emergency_med_authorized is a
// per-application authorization, so we NEVER carry the previous tick).
function studentFromExisting(kid) {
    const uid = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `s-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    return {
        _uid: uid,
        _from_existing_id: kid.id,
        first_name: kid.first_name || '',
        last_name: kid.last_name || '',
        dob: kid.dob || '',
        gender: kid.gender || '',
        swim_level: kid.swim_level || '',
        medical_notes: kid.medical_notes || '',
        // Always false — per-application re-prompt, never carried over.
        emergency_med_authorized: false,
    };
}

// Render "5/2/2018" from a "2018-05-02" ISO DOB. Non-fatal on bad input.
function fmtKidDob(iso) {
    if (!iso || typeof iso !== 'string') return '';
    const parts = iso.split('-');
    if (parts.length !== 3) return iso;
    const [y, m, d] = parts;
    const mm = Number(m); const dd = Number(d);
    if (!mm || !dd || !y) return iso;
    return `${mm}/${dd}/${y}`;
}

export default function ParentRegistration() {
    const { t: t42 } = useLocaleR42();
    const { t: tr40 } = useLocaleR40();
    const { slug } = useParams();
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const { user, refresh } = useAuth();
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [step, setStep] = useState(0);
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);

    const [account, setAccount] = useState({ email: '', password: '' });
    const [guardian, setGuardian] = useState({
        first_name: '', last_name: '', phone: '',
        emergency_contact_name: '', emergency_contact_phone: '',
        address_street: '', address_city: '', address_state: '', address_zip: '',
        address: '', // legacy single-line, auto-composed from parts
        how_did_you_hear: '',
    });
    const [students, setStudents] = useState([emptyStudent()]);
    const [packageId, setPackageId] = useState(params.get('pkg') || '');
    const [preferredSlot, setPreferredSlot] = useState('');
    // Phase 33 — Class scheduling. If the chosen package defines class_slots[]
    // the parent picks ONE slot here. Live capacity is fetched from the
    // public coach endpoint so we can disable full slots before submit.
    const [classSlotId, setClassSlotId] = useState('');
    const [slotCapacity, setSlotCapacity] = useState({}); // { slot_id: { capacity, taken } }
    // Phase 46 / Kayla-fix — single-lesson packages need a date+time picked
    // by the parent at register time so the backend can materialize the
    // one-off lesson into db.lessons (and the coach's schedule). Format
    // stored is "YYYY-MM-DDTHH:MM" in the coach's local time zone.
    const [singleLessonAt, setSingleLessonAt] = useState('');
    const [waiver, setWaiver] = useState({ signed_name: '', agreed: false, cancellation_agreed: false });
    // Legal acceptance — required for new parents (existing logged-in
    // parents already agreed when they first signed up and will be
    // re-prompted via the LegalAcceptanceModal at version bumps). We
    // fetch the current version from the backend so the consent we record
    // matches the wording the parent actually saw.
    const [agreedLegal, setAgreedLegal] = useState(false);
    const [legalVersion, setLegalVersion] = useState('');
    // COPPA — explicit guardian consent for processing minor data. Always
    // required (per-application, not per-account) so we capture it for both
    // brand-new and returning parents. Shown on the Students step where the
    // minors are actually listed.
    const [guardianConsent, setGuardianConsent] = useState(false);
    // SMS opt-in — OPTIONAL. Defaults OFF. The parent must take an
    // affirmative action (tick the checkbox) to enable SMS. We persist
    // the verbatim disclosure text they saw so we have an exact audit
    // trail for Twilio / 10DLC carrier reviews.
    const [smsOptIn, setSmsOptIn] = useState(false);
    // Multi-location preferences (Phase 29). Captured at apply-time and
    // sent to the backend so the coach sees the parent's pick on the
    // application row. Both fields default to None.
    const [preferredLocationId, setPreferredLocationId] = useState('');
    const [deliveryPreference, setDeliveryPreference] = useState('');
    // submitError is set when the auto-submit on Confirmation fails so we can render
    // a recoverable error UI with a Back button instead of leaving the parent
    // staring at a "Submitting…" spinner with no escape.
    const [submitError, setSubmitError] = useState(null);
    // === Stripe Connect (new marketplace flow) ===========================
    // stripeConfig holds the platform publishable key fetched once on
    // mount. paymentResult is populated by PaymentStep when the parent
    // successfully confirms a PaymentIntent (one-time) or SetupIntent
    // (recurring / per-lesson). We forward the resulting IDs to /register
    // so the backend can mark the enrollment paid and (for recurring)
    // create the Subscription server-side.
    const [stripePublishableKey, setStripePublishableKey] = useState('');
    const [paymentResult, setPaymentResult] = useState(null);
    // Phase 87 — apply-time application fee (registration/assessment).
    // feeResult = {setup_intent_id} once the parent's card is saved via the
    // Fee step's SetupIntent (card saved now, CHARGED only on approval).
    // feeSkipped = server said no fee is due for this parent (fee disabled,
    // returning family under first_only scope, Stripe not connected) — the
    // Fee step removes itself from visibleSteps, landing on Confirmation.
    const [feeResult, setFeeResult] = useState(null);
    const [feeSkipped, setFeeSkipped] = useState(false);
    // Phase 98 — registration-fee discount code (applied or fully waiving).
    // Sent in the /register payload; server re-validates it.
    const [feeCode, setFeeCode] = useState('');
    const titleRef = useRef(null);

    // Phase 76k — Bypass detection for returning approved parents.
    // If the visitor is signed in AND already has an approved (or
    // awaiting_payment) application with this coach, hide the whole
    // registration wizard and show a "You're already approved — go
    // book a class" bypass card at the top of the page. Prevents the
    // duplicate-application trap Scott's swimmers were falling into.
    // Silently no-ops for logged-out visitors (undefined `user`) and
    // pending / declined / dismissed / new applicants (existingApp
    // stays null → wizard renders normally).
    const [existingApp, setExistingApp] = useState(null);   // {status, id, decided_at, ...}
    const [existingAppChecked, setExistingAppChecked] = useState(false);
    useEffect(() => {
        if (!user || user.role !== 'parent') {
            setExistingApp(null);
            setExistingAppChecked(true);
            return;
        }
        let cancelled = false;
        api.get('/parent/applications')
            .then((r) => {
                if (cancelled) return;
                const rows = Array.isArray(r.data) ? r.data : [];
                const mine = rows.find((row) => row?.coach?.slug === slug);
                setExistingApp(mine || null);
            })
            .catch(() => { setExistingApp(null); })
            .finally(() => { if (!cancelled) setExistingAppChecked(true); });
        return () => { cancelled = true; };
    }, [slug, user]);
    const bypassReady = !!existingApp && ['approved', 'awaiting_payment'].includes(existingApp.status);
    const bypassPending = !!existingApp && existingApp.status === 'pending';

    // Application-mode is the new default. Legacy "register-with-package" mode is only
    // used if the URL explicitly provides ?pkg=... (e.g. for already-approved parents
    // returning via a deep link). The wizard becomes a 5-step APPLICATION:
    // Account → Guardian (with address) → Students → Waiver → Submitted.
    const isApplicationMode = !params.get('pkg');

    useEffect(() => {
        // Phase 73g — when the URL has ?pkg=<id> we may be hitting a
        // private (link-only) package the coach shared directly. Pass
        // include_private so the backend surfaces that one specific
        // private package alongside the public listings.
        const initialPkg = params.get('pkg');
        const url = initialPkg
            ? `/public/coach/${slug}?include_private=${encodeURIComponent(initialPkg)}`
            : `/public/coach/${slug}`;
        api.get(url)
            .then(r => {
                setData(r.data);
                if (!packageId && r.data.packages?.length) setPackageId(r.data.packages[0].id);
            })
            .catch(e => setError(e?.response?.data?.detail || 'Coach not found'));
    }, [slug]); // eslint-disable-line

    useEffect(() => {
        // if user is parent already, prefill
        if (user?.role === 'parent' && user.parent_profile) {
            setGuardian(g => ({
                first_name: user.parent_profile.first_name || g.first_name,
                last_name: user.parent_profile.last_name || g.last_name,
                phone: user.parent_profile.phone || g.phone,
                emergency_contact_name: user.parent_profile.emergency_contact_name || g.emergency_contact_name,
                emergency_contact_phone: user.parent_profile.emergency_contact_phone || g.emergency_contact_phone,
                address_street: user.parent_profile.address_street || g.address_street,
                address_city: user.parent_profile.address_city || g.address_city,
                address_state: user.parent_profile.address_state || g.address_state,
                address_zip: user.parent_profile.address_zip || g.address_zip,
                address: user.parent_profile.address || g.address,
                how_did_you_hear: g.how_did_you_hear,
            }));
        }
    }, [user]);

    // Phase 77a (Scott's UX ask, Oct 2026) — Inline "Your children" picker.
    // Fetch the parent's existing kids so they can TICK who's applying for
    // this new coach instead of retyping name/DOB/gender/medical every
    // time. Backend already dedups students by (first_name, last_name,
    // dob), so re-submitting an existing kid's data safely updates the
    // same row rather than creating a duplicate.
    //
    // Two-list model on this step:
    //   - myExistingKids[]      : read-only source list from /parent/students
    //   - selectedExistingIds   : which of those the parent has ticked
    //     (drives the corresponding Student card in `students`)
    //   - students[]            : the actual payload we submit. Each entry
    //     may be sourced from an existing kid (marker: _from_existing_id)
    //     or freshly typed via "Add another student".
    //
    // Per-application re-prompt policy: even when we prefill from an
    // existing kid, we RESET `emergency_med_authorized` to false so the
    // guardian must re-authorize per application (safest for the coach).
    // The Waiver step separately re-prompts a fresh typed signature for
    // every coach the parent applies to.
    const [myExistingKids, setMyExistingKids] = useState([]);
    const [existingKidsLoaded, setExistingKidsLoaded] = useState(false);
    useEffect(() => {
        if (!user || user.role !== 'parent') {
            setMyExistingKids([]);
            setExistingKidsLoaded(true);
            return;
        }
        let cancelled = false;
        api.get('/parent/students')
            .then((r) => {
                if (cancelled) return;
                const rows = Array.isArray(r.data) ? r.data : [];
                // Filter out soft-deleted rows just in case the backend
                // ever forgets. UI needs stable IDs and at least a name.
                const cleaned = rows.filter((s) => s && s.id && (s.first_name || s.last_name));
                setMyExistingKids(cleaned);
            })
            .catch(() => { setMyExistingKids([]); })
            .finally(() => { if (!cancelled) setExistingKidsLoaded(true); });
        return () => { cancelled = true; };
    }, [user]);

    // When we discover the parent has existing kids AND the students[]
    // array is still the pristine single-empty-student we seed at mount,
    // clear it so the picker isn't competing with an empty typed card.
    // Parent will either tick an existing kid or press "Add another
    // student" to type a brand-new one. Only runs ONCE right after load.
    useEffect(() => {
        if (!existingKidsLoaded) return;
        if (myExistingKids.length === 0) return;
        setStudents((arr) => {
            if (arr.length !== 1) return arr;
            const s = arr[0];
            const isPristine = !s.first_name && !s.last_name && !s.dob && !s.gender && !s.swim_level && !s.medical_notes && !s.emergency_med_authorized && !s._from_existing_id;
            return isPristine ? [] : arr;
        });
    }, [existingKidsLoaded, myExistingKids.length]);

    useEffect(() => {
        if (titleRef.current) titleRef.current.focus();
    }, [step]);

    // Fetch the current legal version so first-time parents agree to
    // exactly the version the audit log will record.
    useEffect(() => {
        api.get('/public/legal').then((r) => setLegalVersion(r.data?.version || '')).catch(() => {
            /* non-fatal — backend will reject without a current version */
        });
    }, []);

    // Fetch the Stripe publishable key once. PaymentStep needs it to
    // initialize Stripe.js scoped to the SplashPad platform account.
    // Non-fatal on failure — without a key we silently fall back to the
    // legacy external-link / manual-invoice payment branches.
    useEffect(() => {
        api.get('/public/stripe-config')
            .then((r) => setStripePublishableKey(r.data?.publishable_key || ''))
            .catch(() => { /* coach hasn't connected — legacy flow handles it */ });
    }, []);

    // Phase 33 — Fetch live slot capacity for the selected package's
    // class_slots so the UI can render `taken/cap · N seats left` chips
    // and disable full slots before the parent submits.
    useEffect(() => {
        if (!packageId || !slug) { setSlotCapacity({}); return; }
        let cancelled = false;
        api.get(`/public/coach/${slug}/package/${packageId}/slot-capacity`)
            .then((r) => {
                if (cancelled) return;
                const map = {};
                for (const row of (r.data?.slots || [])) {
                    map[row.slot_id] = { capacity: row.capacity || 0, taken: row.taken || 0 };
                }
                setSlotCapacity(map);
            })
            .catch(() => { if (!cancelled) setSlotCapacity({}); });
        return () => { cancelled = true; };
    }, [packageId, slug]);


    const coach = data?.coach;
    // Memoize `settings` so the visibleSteps useMemo below has a stable
    // dependency. Without this, `coach?.settings || {}` produced a brand
    // new `{}` reference on every render whenever settings was missing,
    // which busted the memo and re-computed step order constantly.
    const settings = useMemo(() => coach?.settings || {}, [coach?.settings]);

    // Phase 99 — "Pay Another Way" (Venmo/Zelle/cash). Offered on the
    // registration Fee step and on package Payment steps — NEVER for
    // class packages (class_slots present ⇒ card only).
    const payAnotherWay = useMemo(() => ({
        enabled: !!settings.pay_another_way_enabled,
        instructions: settings.pay_another_way_instructions || '',
    }), [settings.pay_another_way_enabled, settings.pay_another_way_instructions]);
    const packages = data?.packages || [];
    const selectedPkg = packages.find(p => p.id === packageId);

    // Determine which steps are visible based on settings + application/enrollment mode
    const visibleSteps = useMemo(() => {
        const steps = [];
        // Step 0: Account (skip if logged in)
        if (!user) steps.push('Account');
        steps.push('Guardian');
        steps.push('Students');
        // Multi-location step — only shown when there's something to pick:
        //   - fixed coach with >1 active location, OR
        //   - "both" coach (parent picks delivery_preference + optional location).
        // Mobile-only coaches and fixed coaches with a single location skip it.
        const sm = (coach?.service_mode || 'fixed').toLowerCase();
        const locs = coach?.locations || [];
        const showLocationStep = (sm === 'fixed' && locs.length > 1) || sm === 'both';
        if (showLocationStep) steps.push('Location');
        if (!isApplicationMode) {
            // Legacy enrollment flow (only when ?pkg=... is in URL)
            steps.push('Package');
            if (settings?.scheduling_enabled) steps.push('Schedule');
            // Phase 33 — Class scheduling. When the selected package has
            // class_slots[] defined, insert a "Class time" step so the
            // parent picks an exact occurrence (e.g. Tue 4pm) before paying.
            if (selectedPkg && (selectedPkg.class_slots || []).length > 0) {
                steps.push('ClassTime');
            }
            // Phase 46 / Kayla-fix — Single-lesson packages without
            // class_slots[] need the parent to pick a one-off date+time
            // for that lesson; otherwise the backend creates an enrollment
            // but no lesson row, and the coach never sees the lesson on
            // their schedule. Only insert this step when there are no
            // class_slots (those already pin a time via ClassTime).
            const _lt = (selectedPkg?.lesson_type || '').toLowerCase();
            const _hasSlots = (selectedPkg?.class_slots || []).length > 0;
            const _isSingle = _lt === 'single_lesson' || (
                !_lt
                && (selectedPkg?.billing_type === 'one_time')
                && Number(selectedPkg?.session_count || 0) === 1
            );
            if (selectedPkg && _isSingle && !_hasSlots) {
                steps.push('SingleLessonTime');
            }
        }
        if (settings?.require_waiver !== false) steps.push('Waiver');
        // Phase 87 — application fee step (application mode only). Deep
        // eligibility (amount, first_only scope for returning families) is
        // decided server-side by /application-fee/init; when it says "no fee
        // due" the step flags feeSkipped which removes it from this list —
        // the current index then points at the next step automatically.
        if (
            isApplicationMode
            && settings?.application_fee_enabled
            && coach?.stripe_connect_status === 'enabled'
            && !!stripePublishableKey
            && !feeSkipped
        ) {
            steps.push('Fee');
        }
        // Phase 68b (July 2026, Jacob Gabala support ticket) — respect
        // per-package `require_payment_for_enrollment`. When the coach
        // has picked "Enroll immediately, collect payment later" on this
        // specific package, SKIP the Payment step entirely — the parent
        // will get an invoice they can pay at their leisure from the
        // Payments tab. This mirrors the coach-portal enroll flow's
        // pay-later branch.
        //
        // We treat the field as strict-mode ONLY when explicitly True
        // (backend default is False → lenient). Missing/undefined =
        // lenient, matching what the Coach's Package UI shows as the
        // default option.
        const _pkgRequiresPayFirst = selectedPkg?.require_payment_for_enrollment === true;
        const _pkgIsFree = Number(selectedPkg?.price_cents || 0) <= 0;
        const _showPayment = !isApplicationMode
            && settings?.online_payment !== false
            && _pkgRequiresPayFirst
            && !_pkgIsFree;
        if (_showPayment) steps.push('Payment');
        steps.push('Confirmation');
        return steps;
    }, [user, settings, isApplicationMode, coach?.service_mode, coach?.locations, selectedPkg, coach?.stripe_connect_status, stripePublishableKey, feeSkipped]);

    const stepName = visibleSteps[step] || 'Account';
    const totalSteps = visibleSteps.length || 1;
    const progressVal = Math.round(((step + 1) / totalSteps) * 100);

    // === Stripe Connect detection ========================================
    // We render the in-app Stripe Elements (PaymentStep) only when ALL
    // of the following are true:
    //   1) the coach has finished Stripe Connect Express onboarding
    //      (stripe_connect_status === 'enabled' on the public coach payload)
    //   2) the selected package was authored on the new schema
    //      (billing_type set: one_time / recurring_monthly / per_lesson)
    //   3) we successfully fetched the platform publishable key
    // Otherwise we keep the legacy review-only Payment step that drives
    // external-link or manual-invoice flows.
    const usesConnectCheckout = useMemo(() => {
        if (!coach || !selectedPkg) return false;
        if (coach.stripe_connect_status !== 'enabled') return false;
        const bt = (selectedPkg.billing_type || '').toString();
        if (!['one_time', 'recurring_monthly', 'per_lesson'].includes(bt)) return false;
        if (!stripePublishableKey) return false;
        return true;
    }, [coach, selectedPkg, stripePublishableKey]);

    // Phase 77a — Fast-lookup set of existing kid IDs the parent has
    // currently ticked in the "Your children" picker. Declared here
    // (above all early returns) so Rules of Hooks stay satisfied even
    // when we short-circuit render for the error/loading branches below.
    const selectedExistingIds = useMemo(
        () => new Set(students.filter((s) => s._from_existing_id).map((s) => s._from_existing_id)),
        [students],
    );

    // Auto-submit when entering Confirmation step (must be before any early returns).
    // Skip when a previous submit already errored — otherwise we'd infinite-loop the
    // failing request and the user would never see the error UI.
    useEffect(() => {
        if (stepName === 'Confirmation' && !result && !submitting && !submitError && data) submit();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stepName, data]);

    if (error) {
        return (
            <div className="min-h-dvh flex items-center justify-center bg-[#f4f7fb] p-6">
                <Card className="sd-card max-w-md p-6 text-center">
                    <h1 className="font-display text-xl font-bold text-[#0a1628]">{tr40("Page unavailable")}</h1>
                    <p className="mt-2 text-[#1a3a5c]">{error}</p>
                    <Button asChild className="sd-cta mt-4"><Link to="/">{tr40("Home")}</Link></Button>
                </Card>
            </div>
        );
    }

    if (!data) return <div className="p-6 text-center">{tr40("Loading…")}</div>;

    // If we somehow ended up on a step that no longer exists (e.g., auth state
    // flipped mid-flow and shrank visibleSteps), clamp the step pointer.
    const safeStep = Math.min(step, visibleSteps.length - 1);
    if (safeStep !== step) {
        setTimeout(() => setStep(safeStep), 0);
    }

    const next = () => {
        // basic validation per step
        if (stepName === 'Account') {
            if (!account.email || !account.password) { toast.error(tr40("Email and password required")); return; }
            if (account.password.length < 10) { toast.error(tr40("Password must be at least 10 characters")); return; }
            if (!/[A-Za-z]/.test(account.password) || !/\d/.test(account.password)) {
                toast.error(tr40("Password must include at least one letter and one number."));
                return;
            }
            // Legal acceptance is required to create a parent account.
            // Returning logged-in parents never see the Account step at
            // all (visibleSteps drops it), so we only validate it here.
            if (!agreedLegal) {
                toast.error(tr40("Please review and accept the Terms of Service and Privacy Policy to continue."));
                return;
            }
        }
        if (stepName === 'Guardian') {
            // Per-field validation so the parent knows EXACTLY what's missing
            // instead of seeing a generic "fill required fields" message.
            if (!guardian.first_name) { toast.error(tr40("Please enter your first name.")); return; }
            if (!guardian.last_name) { toast.error(tr40("Please enter your last name.")); return; }
            if (!guardian.phone) { toast.error(tr40("Please enter your phone number — the coach uses this to reach you.")); return; }
            // Phase A2P — enforce a minimum of 10 digits so the phone can
            // actually be delivered to via SMS. Twilio's audit rejects
            // brand/campaign registrations whose signup form doesn't
            // validate this at the client boundary.
            if (!isValidPhone(guardian.phone)) {
                toast.error(tr40("Please enter a valid mobile phone number (at least 10 digits)."));
                return;
            }
            if (!guardian.address_street) { toast.error(tr40("Please enter your street address.")); return; }
            if (!guardian.address_city) { toast.error(tr40("Please enter your city.")); return; }
            if (!guardian.address_state) { toast.error(tr40("Please enter your state (2-letter code, e.g. CA).")); return; }
            if (!guardian.address_zip) { toast.error(tr40("Please enter your ZIP code.")); return; }
        }
        if (stepName === 'Students') {
            if (!students.length) {
                toast.error(tr40("Please pick at least one child from your account or add a new one."));
                return;
            }
            for (const s of students) {
                if (!s.first_name || !s.last_name || !s.dob) { toast.error(tr40("Each student needs a name and date of birth")); return; }
            }
            // COPPA — guardian must explicitly affirm consent for the listed
            // minors. We enforce this at the next() guard so the parent can't
            // proceed without ticking the box.
            if (!guardianConsent) {
                toast.error(tr40("Please confirm you are the parent/legal guardian of the listed children."));
                return;
            }
        }
        if (stepName === 'Location') {
            // Validate location pick. For 'both' coaches the parent
            // must at least pick a delivery preference; if they chose
            // 'fixed' or 'any' AND the coach has locations, they must
            // also pick one.
            const sm = (coach?.service_mode || 'fixed').toLowerCase();
            const locs = coach?.locations || [];
            if (sm === 'fixed' && locs.length > 1 && !preferredLocationId) {
                toast.error(tr40("Please pick the location you’d like to attend."));
                return;
            }
            if (sm === 'both') {
                if (!deliveryPreference) {
                    toast.error(tr40("Please pick whether you’d like fixed-location or mobile lessons."));
                    return;
                }
                if (deliveryPreference !== 'mobile' && locs.length > 1 && !preferredLocationId) {
                    toast.error(tr40("Please pick the location you’d like to attend."));
                    return;
                }
            }
        }
        if (stepName === 'Package') {
            if (!packageId) { toast.error(tr40("Pick a package")); return; }
        }
        if (stepName === 'ClassTime') {
            if (!classSlotId) { toast.error(tr40("Pick a class time to continue.")); return; }
            // Defensive client-side capacity check; backend re-validates.
            const cap = slotCapacity[classSlotId]?.capacity ?? 0;
            const taken = slotCapacity[classSlotId]?.taken ?? 0;
            if (cap > 0 && taken >= cap) {
                toast.error(tr40("That class just filled up. Pick another time."));
                setClassSlotId('');
                return;
            }
        }
        if (stepName === 'SingleLessonTime') {
            // Phase 46 / Kayla-fix — require a date+time before the parent
            // proceeds to payment. Backend re-validates; the in-page hint
            // explains why the picker is required.
            if (!singleLessonAt) {
                toast.error(tr40("Pick a date and time for the lesson to continue."));
                return;
            }
            const picked = new Date(singleLessonAt);
            if (isNaN(picked.getTime())) {
                toast.error(tr40("That date/time is invalid — try picking it from the calendar again."));
                return;
            }
            // Block obviously-past picks. Backend allows a 1h grace; we
            // gate at "now" so the error is visible before payment.
            if (picked.getTime() < Date.now() - 60 * 60 * 1000) {
                toast.error(tr40("Lesson time is in the past. Pick a future date and time."));
                return;
            }
        }
        if (stepName === 'Waiver') {
            if (!waiver.agreed || !waiver.cancellation_agreed) { toast.error(tr40("Please agree to the Liability Waiver and Cancellation Policy to continue")); return; }
            if (!waiver.signed_name.trim()) { toast.error(tr40("Type your full name to sign")); return; }
        }
        if (stepName === 'Payment') {
            // For the new Stripe Connect flow we GATE the Continue button
            // until the parent successfully confirms a card via Stripe
            // Elements (PaymentStep populates paymentResult on success).
            // Legacy flows (external-link / manual-invoice) don't need a
            // gate — they just show a review card.
            if (usesConnectCheckout && !paymentResult) {
                toast.error(tr40("Please complete payment before continuing."));
                return;
            }
        }
        if (stepName === 'Fee') {
            // Phase 87 — the application fee step must confirm the
            // SetupIntent (card saved) before the application submits.
            if (!feeResult) {
                toast.error(tr40("Please save your card to continue."));
                return;
            }
        }
        setStep(s => Math.min(s + 1, totalSteps - 1));
    };

    const prev = () => setStep(s => Math.max(s - 1, 0));

    // Parse a Pydantic 422 error array into a human-readable message + an
    // ordered list of (step, field) pointers so we can route the parent back
    // to the right step with a helpful hint.
    const parsePydanticErrors = (detail) => {
        if (!Array.isArray(detail)) return null;
        const fieldLabels = {
            email: { label: 'Email', step: 'Account' },
            password: { label: 'Password', step: 'Account' },
            'guardian.first_name': { label: 'First name', step: 'Guardian' },
            'guardian.last_name': { label: 'Last name', step: 'Guardian' },
            'guardian.phone': { label: 'Phone number', step: 'Guardian' },
            'guardian.address_street': { label: 'Street address', step: 'Guardian' },
            'guardian.address_city': { label: 'City', step: 'Guardian' },
            'guardian.address_state': { label: 'State', step: 'Guardian' },
            'guardian.address_zip': { label: 'ZIP code', step: 'Guardian' },
        };
        const issues = [];
        for (const err of detail) {
            // err.loc looks like ['body','guardian','phone'] or ['body','email']
            const loc = Array.isArray(err?.loc) ? err.loc.slice(1) : [];
            const key = loc.join('.');
            const meta = fieldLabels[key];
            if (meta) {
                issues.push({ field: key, label: meta.label, step: meta.step, msg: err?.msg || 'is invalid' });
            } else if (key) {
                issues.push({ field: key, label: key, step: null, msg: err?.msg || 'is invalid' });
            }
        }
        return issues.length ? issues : null;
    };

    const submit = async () => {
        setSubmitting(true);
        setSubmitError(null);
        try {
            // Compose legacy single-line address from parts for backward compat
            const composedAddress = [
                guardian.address_street,
                [guardian.address_city, guardian.address_state, guardian.address_zip].filter(Boolean).join(' '),
            ].filter(Boolean).join(', ');
            const guardianPayload = { ...guardian, address: composedAddress || guardian.address };

            // Strip client-only fields (e.g. _uid, _from_existing_id)
            // before sending to the backend so we don't pollute the API
            // contract with UI state. Backend dedups on (first_name,
            // last_name, dob) — so previously-known kids match the same
            // student row and get updated, never duplicated.
            // eslint-disable-next-line no-unused-vars
            const cleanStudents = students.map(({ _uid, _from_existing_id, ...rest }) => rest);

            const payload = {
                guardian: guardianPayload,
                students: cleanStudents,
                preferred_slot: preferredSlot || null,
                waiver: settings?.require_waiver !== false ? {
                    waiver_text: coach.waiver_text || '',
                    signed_name: waiver.signed_name,
                    agreed: waiver.agreed,
                    cancellation_agreed: waiver.cancellation_agreed,
                    cancellation_general_text: coach.cancellation_general_text || '',
                    cancellation_weather_text: coach.cancellation_weather_text || '',
                    cancellation_sick_text: coach.cancellation_sick_text || '',
                } : null,
            };
            // Only include package_id in legacy enrollment mode
            if (!isApplicationMode && packageId) payload.package_id = packageId;
            // Phase 33 — Class scheduling. Only sent when the chosen
            // package actually defines class_slots[]; backend enforces a
            // 400 if the slot is required but missing.
            if (!isApplicationMode && (selectedPkg?.class_slots || []).length > 0 && classSlotId) {
                payload.class_slot_id = classSlotId;
            }
            // Phase 46 / Kayla-fix — for single_lesson packages without
            // class_slots, forward the parent-picked date+time. Backend
            // validates required-ness and materializes the lesson row.
            if (!isApplicationMode && singleLessonAt) {
                payload.single_lesson_at = singleLessonAt;
            }
            // Always send email + password if the parent typed them in, even if
            // we *think* they're logged in. This is a defensive guard: if their
            // token expired between page load and submit, the backend will fall
            // back to creating/matching the account from these fields instead
            // of returning a confusing "Email and password are required" error.
            if (account.email) payload.email = account.email;
            if (account.password) payload.password = account.password;
            // Send the legal version the parent saw. The backend ignores
            // this for already-authenticated parents and enforces it for
            // first-time parents.
            if (legalVersion) payload.legal_accepted_version = legalVersion;
            // COPPA — server requires this true for every submission (even
            // for returning parents, since consent is per-application).
            payload.guardian_consent_accepted = !!guardianConsent;
            // === SMS opt-in (10DLC/TCR audit trail) =======================
            // Only post the opt-in fields when the parent ticked the box.
            // We send the EXACT disclosure copy they saw so the audit
            // record on the backend matches what carriers will verify
            // against the public /sms-terms page.
            if (smsOptIn) {
                payload.sms_opt_in_accepted = true;
                payload.sms_opt_in_phone = guardian.phone || '';
                payload.sms_opt_in_text = buildSmsOptInDisclosure(guardian.phone);
            }
            // Multi-location preferences (Phase 29). Backend silently
            // drops invalid IDs and locks delivery_preference based on
            // the coach's service_mode, so it's safe to forward as-is.
            if (preferredLocationId) payload.preferred_location_id = preferredLocationId;
            if (deliveryPreference) payload.delivery_preference = deliveryPreference;
            // === Stripe Connect: pipe the confirmed intent IDs through ===
            // When the parent used the in-app Elements step, PaymentStep
            // returned the resulting PaymentIntent or SetupIntent id and
            // (when present) the saved PaymentMethod id. The backend uses
            // these to verify the charge and create the Subscription for
            // recurring packages. Skipped entirely for legacy flows.
            if (paymentResult) {
                if (paymentResult.external) {
                    // Phase 99 — offline payment: no Stripe intents; the
                    // backend creates the enrollment at payment_pending_external.
                    payload.external_payment_requested = true;
                } else if (paymentResult.intent_type === 'payment_intent') {
                    payload.payment_intent_id = paymentResult.intent_id;
                    payload.payment_intent_type = 'payment_intent';
                } else if (paymentResult.intent_type === 'setup_intent') {
                    payload.setup_intent_id = paymentResult.intent_id;
                    payload.payment_intent_type = 'setup_intent';
                }
                if (paymentResult.payment_method_id) {
                    payload.payment_method_id = paymentResult.payment_method_id;
                }
            }
            // Phase 87 — application fee: the Fee step saved the card via
            // SetupIntent; forward its id so the backend verifies it and
            // attaches the pending fee to the application row.
            if (isApplicationMode && feeResult?.setup_intent_id) {
                payload.application_fee_setup_intent_id = feeResult.setup_intent_id;
            }
            // Phase 99 — offline registration fee (Venmo/Zelle/cash).
            if (isApplicationMode && feeResult?.external) {
                payload.application_fee_external = true;
            }
            // Phase 98 — fee discount code (partial via feeResult, or a 100%
            // waiver captured by onWaived below). Server re-validates.
            if (isApplicationMode && (feeCode || feeResult?.fee_discount_code)) {
                payload.application_fee_discount_code = feeCode || feeResult.fee_discount_code;
            }
            // For the strict not-logged-in path, both must be present.
            if (!user && (!payload.email || !payload.password)) {
                toast.error(tr40("Please fill in your email and password before submitting."));
                // Send them back to the Account step so they can fix it.
                const accountStepIdx = visibleSteps.indexOf('Account');
                if (accountStepIdx >= 0) setStep(accountStepIdx);
                setSubmitting(false);
                return;
            }
            const r = await api.post(`/public/coach/${slug}/register`, payload);
            setAuth(r.data.token, r.data.user);
            await refresh();
            setResult(r.data);
            // confetti
            try {
                const colors = ['#00d4ff', '#ff6b35', '#ffd700', '#1a3a5c'];
                confetti({ particleCount: 80, spread: 70, origin: { y: 0.6 }, colors });
                setTimeout(() => confetti({ particleCount: 50, spread: 90, origin: { y: 0.5 }, colors }), 500);
            } catch (confettiErr) {
                /* confetti is decorative; never fail the registration if the animation breaks */
                if (process.env.NODE_ENV !== 'production') console.debug('confetti failed:', confettiErr);
            }
            toast.success(isApplicationMode ? 'Application submitted!' : 'Registration complete!');
        } catch (e) {
            const rawDetail = e?.response?.data?.detail;
            // === 422 Pydantic field errors → route parent back to right step ====
            const pyIssues = parsePydanticErrors(rawDetail);
            if (pyIssues) {
                const summary = pyIssues.map(i => i.label).join(', ');
                const firstStep = pyIssues.find(i => i.step)?.step;
                setSubmitError({
                    title: 'A few details are missing',
                    message: `Please check: ${summary}.`,
                    issues: pyIssues,
                    backStep: firstStep,
                });
                toast.error(`Missing or invalid: ${summary}`);
                setSubmitting(false);
                return;
            }
            const detail = typeof rawDetail === 'string' ? rawDetail : (rawDetail?.toString?.() || 'Submission failed');
            // Phase 33 — Class just filled up while parent was finishing
            // the wizard. Route them back to the ClassTime step so they
            // can pick another slot without losing form state.
            const status = e?.response?.status;
            const looksLikeClassFull = status === 409 || /class.*(filled|full|just)|pick another time|pick a class/i.test(detail);
            if (looksLikeClassFull) {
                const classStepIdx = visibleSteps.indexOf('ClassTime');
                if (classStepIdx >= 0) {
                    toast.error(tr40("Sorry, that class just filled up while you were finishing. Please pick another time."), { duration: 9000 });
                    // Clear the now-stale pick and refresh capacity.
                    setClassSlotId('');
                    setStep(classStepIdx);
                    // Re-fetch live capacity so the UI updates.
                    try {
                        const r2 = await api.get(`/public/coach/${slug}/package/${packageId}/slot-capacity`);
                        const map = {};
                        for (const row of (r2.data?.slots || [])) {
                            map[row.slot_id] = { capacity: row.capacity || 0, taken: row.taken || 0 };
                        }
                        setSlotCapacity(map);
                    } catch { /* non-fatal */ }
                    setSubmitting(false);
                    return;
                }
            }
            // === Specific error → actionable recovery =======================
            // Stale-token edge case: frontend thought parent was logged in
            // (hid the Account step) but the JWT was actually expired, so the
            // backend never saw the auth header and now demands an email +
            // password. Route them to the Account step instead of just
            // throwing a confusing toast.
            const isEmailRequired = /email.*password.*required|email and password/i.test(detail);
            if (isEmailRequired) {
                toast.error(tr40("Looks like your sign-in expired. Please fill in your email and password to finish your application."), { duration: 9000 });
                // Force-clear stale auth (BOTH sd_token AND sd_user) and
                // show the Account step on next render. Using clearAuth()
                // instead of just removing sd_token — otherwise the
                // cached user object survives the reload below and the
                // form thinks the parent is still logged in, landing
                // them on an empty Guardian step instead of Account.
                try { clearAuth(); } catch { /* ignore */ }
                const accountStepIdx = visibleSteps.indexOf('Account');
                if (accountStepIdx >= 0) {
                    setStep(accountStepIdx);
                } else {
                    // visibleSteps didn't include Account because we thought
                    // the user was logged in. Reload the page so the steps
                    // re-compute with no user → Account step reappears.
                    window.location.reload();
                }
                return;
            }
            // Special-case: parent already has a SplashPad account.
            // We let them apply to multiple coaches from one login, so surface
            // a one-tap "Log in" path back to this same coach's page.
            const looksLikeAccountExists = /already (?:exists|registered)|account.*exists|please log in/i.test(detail);
            if (!user && looksLikeAccountExists) {
                const returnTo = `/coach/${slug}/register`;
                toast.error(tr40("You already have a SplashPad account — log in to apply with this coach."), {
                    duration: 9000,
                    action: {
                        label: 'Log in',
                        onClick: () => navigate(`/login?from=${encodeURIComponent(returnTo)}`),
                    },
                });
                setSubmitError({
                    title: 'You already have an account',
                    message: detail,
                    issues: [],
                    backStep: 'Account',
                });
            } else {
                toast.error(detail);
                setSubmitError({
                    title: 'Submission failed',
                    message: detail,
                    issues: [],
                    backStep: visibleSteps[0],
                });
            }
        } finally {
            setSubmitting(false);
        }
    };

    const updateStudent = (i, patch) => setStudents(arr => arr.map((s, idx) => idx === i ? { ...s, ...patch } : s));
    const addStudent = () => setStudents(arr => [...arr, emptyStudent()]);
    const removeStudent = (i) => setStudents(arr => arr.filter((_, idx) => idx !== i));

    // Phase 77a — Toggle an existing kid from the "Your children" picker
    // into/out of the students[] payload. When ticked we prefill from
    // /parent/students; when unticked we splice the row back out. Idempotent.
    const toggleExistingKid = (kid, checked) => {
        setStudents((arr) => {
            const already = arr.find((s) => s._from_existing_id === kid.id);
            if (checked && !already) {
                return [...arr, studentFromExisting(kid)];
            }
            if (!checked && already) {
                return arr.filter((s) => s._from_existing_id !== kid.id);
            }
            return arr;
        });
    };

    return (
        <div className="min-h-dvh bg-[#f4f7fb] flex flex-col">
            {/* SEO audit fix — per-coach registration pages are conversion
                funnels, not search destinations: unique title, canonical
                consolidated onto the coach's public page, noindex. */}
            <Seo
                title={`Register with ${coach?.display_name || 'your coach'} | SplashPad`}
                description={`Sign up for swim lessons with ${coach?.display_name || 'your coach'} — create your parent account, sign the waiver, and pay securely online.`}
                canonical={`https://splashpad.pro/coach/${slug}`}
                noIndex
            />
            {/* Sticky stepper — coach pill anchors the parent's awareness of
                WHO they're registering with on every step. Falls back to the
                SplashPad logo if for some reason the coach payload is missing
                (e.g. a slow first load before /public/coach resolves).

                Phase 76j (Aug 2026) — Added a persistent "Log in" affordance
                on the right side of the header. Previously the "already have
                an account" hint lived only inside the Account step's card,
                which meant a returning parent who was already on step 2+
                had no visible way to jump into their existing account —
                they'd bounce, refresh, or start a duplicate application.
                Header link is visible on EVERY step, both mobile and desktop.
                Hidden only when we already know the parent is signed in
                (the logged-in banner below handles that case). */}
            <header className="sticky top-0 z-30 sd-glass border-b">
                <div className="mx-auto max-w-3xl px-4 py-3 sm:px-6">
                    <div className="flex items-center justify-between gap-3">
                        {coach?.display_name ? (
                            <Link to={`/coach/${slug}`} className="shrink-0" data-testid="reg-header-coach-link">
                                <CoachIntroPill coach={coach} />
                            </Link>
                        ) : (
                            <Link to={`/coach/${slug}`} className="shrink-0"><Logo size="sm" /></Link>
                        )}
                        <div className="flex-1">
                            <div className="text-xs font-semibold text-[#1a3a5c]">{tr40('Step {current} of {total}', { current: step + 1, total: totalSteps })} · {tr40(stepName)}</div>
                            <Progress value={progressVal} className="mt-1 h-2" data-testid="registration-progress-bar" />
                        </div>
                        {!user && (
                            <Link
                                to={`/login?from=${encodeURIComponent(`/coach/${slug}`)}`}
                                className="shrink-0 inline-flex items-center gap-1 rounded-lg border-2 border-[#0a1628] bg-white px-3 py-1.5 text-xs font-semibold text-[#0a1628] hover:bg-[#0a1628] hover:text-white sd-focus"
                                data-testid="reg-header-login-link"
                                aria-label={tr40("Log in to an existing SplashPad account")}
                            >
                                <LogIn className="h-3.5 w-3.5" />
                                <span>{tr40("Log in")}</span>
                            </Link>
                        )}
                    </div>
                </div>
            </header>

            {/* Phase 76j — Persistent "returning parent" banner. Sits directly
                below the sticky header on ALL steps (only when logged out),
                so a parent who realizes mid-form they already have an account
                doesn't have to hunt for the button. Complementary to the
                header link; belt-and-braces because parents scan the top of
                the page differently than the header chrome. */}
            {!user && (
                <div className="border-b border-[#00d4ff]/40 bg-[#e6fbff]" data-testid="reg-existing-account-banner">
                    <div className="mx-auto flex max-w-3xl flex-wrap items-center justify-between gap-2 px-4 py-2 text-sm sm:px-6">
                        <span className="text-[#0a1628]">
                            <strong>{tr40("Already a SplashPad parent?")}</strong> {tr40("No need to re-apply — log in and you're good to go.")} </span>
                        <Link
                            to={`/login?from=${encodeURIComponent(`/coach/${slug}`)}`}
                            className="inline-flex items-center gap-1 rounded-lg border-2 border-[#0a1628] bg-white px-3 py-1 text-xs font-semibold text-[#0a1628] hover:bg-[#0a1628] hover:text-white sd-focus"
                            data-testid="reg-existing-account-banner-cta"
                            aria-label={tr40("Log in to your existing account")}
                        >
                            <LogIn className="h-3.5 w-3.5" /> {tr40("Log in")} </Link>
                    </div>
                </div>
            )}

            {/* === Logged-in banner ====================================
                When a parent is already signed in, we hide the email/password
                step. Surface their identity prominently so they're never
                confused about which account their application will land in. */}
            {user && (
                <div className="border-b border-[#00d4ff]/30 bg-[#e6fbff]/70" data-testid="reg-loggedin-banner">
                    <div className="mx-auto flex max-w-3xl flex-wrap items-center justify-between gap-2 px-4 py-2 text-sm sm:px-6">
                        <span className="text-[#0a1628]"> {tr40("Signed in as")} <strong>{user.email}</strong>{tr40(". Your application will be tied to this account.")} </span>
                        <button
                            type="button"
                            onClick={() => {
                                // User wants to sign in with a different account.
                                // Wipe BOTH auth keys (sd_token + sd_user) so the
                                // login page renders in a clean state, then route
                                // them to /login with a `next=` param so a
                                // successful login lands them right back on this
                                // same registration page mid-flow.
                                //
                                // Earlier behaviour was `clearAuth() + reload()`,
                                // which just re-rendered the form as a fresh
                                // signup — that's NOT what parents who already
                                // have an account want. They want to LOG IN as
                                // a different person, not start a brand-new
                                // account from scratch.
                                try { clearAuth(); } catch {}
                                const returnTo = `/coach/${slug}/register`;
                                navigate(
                                    `/login?from=${encodeURIComponent(returnTo)}&next=${encodeURIComponent(returnTo)}`,
                                    { replace: true }
                                );
                            }}
                            className="text-xs font-semibold text-[#0a1628] underline underline-offset-2 hover:text-[#1a3a5c]"
                            data-testid="reg-loggedin-banner-switch"
                        > {tr40("Not you? Log in with a different account")} </button>
                    </div>
                </div>
            )}

            {/* Phase 73j (Wingman report) — bottom padding to keep last form
                fields above the sticky Back/Continue footer on mobile.
                Same calculation as Onboarding.jsx (~90px footer on iPhone,
                ~60px on desktop). pb-32 reads as roomy on a 390-wide
                viewport without feeling stretched on tablets. */}
            <main className="flex-1 px-4 py-6 pb-32 sm:px-6 sm:pb-12">
                <div className="mx-auto max-w-3xl">
                    {/* Phase 76k — Bypass cards. Approved-already parents
                        who click a coach's shareable link land here fully
                        signed in but the wizard would otherwise force them
                        through a duplicate application. Instead we show a
                        clear "you're already in" card with a big Go-to-
                        Booking CTA, and hide the whole wizard beneath.
                        For pending applicants we show a softer info card
                        instead of forcing re-entry. */}
                    {existingAppChecked && bypassReady && (
                        <Card className="sd-card mb-6 border-2 border-[#00d4ff]/50 bg-gradient-to-br from-white to-[#e6fbff] p-6" data-testid="reg-bypass-approved-card">
                            <div className="flex items-start gap-3">
                                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#00d4ff]/20 text-[#0a1628]">
                                    <Check className="h-5 w-5" />
                                </div>
                                <div className="flex-1">
                                    <h2 className="font-display text-xl font-bold text-[#0a1628]" data-testid="reg-bypass-title"> {tr40("You're already approved with")} {coach?.display_name || 'this coach'}!
                                    </h2>
                                    <p className="mt-1 text-sm text-[#1a3a5c]/80"> {tr40("No need to reapply — head straight to the booking page to pick a class")}{existingApp?.status === 'awaiting_payment' ? tr40("and complete payment") : ''}.
                                    </p>
                                    <div className="mt-4 flex flex-wrap gap-2">
                                        <Button
                                            asChild
                                            className="sd-cta sd-focus h-11 rounded-xl"
                                            data-testid="reg-bypass-go-to-booking"
                                        >
                                            <Link to={`/parent/coach/${slug}/book`}> {tr40("Go to Booking")} <ArrowRight className="ml-1 h-4 w-4" />
                                            </Link>
                                        </Button>
                                        <Button
                                            asChild
                                            variant="outline"
                                            className="h-11 rounded-xl"
                                            data-testid="reg-bypass-view-enrollments"
                                        >
                                            <Link to="/parent/enrollments"> {tr40("My Enrollments")} </Link>
                                        </Button>
                                    </div>
                                    <p className="mt-3 text-xs text-[#1a3a5c]/60"> {tr40("Need to add another swimmer to your account? Head to")} <Link to="/parent/profile" className="underline">{tr40("your profile")}</Link> {tr40("instead.")} </p>
                                </div>
                            </div>
                        </Card>
                    )}
                    {existingAppChecked && bypassPending && (
                        <Card className="sd-card mb-6 border-2 border-[#ffd700]/60 bg-gradient-to-br from-white to-[#fffbe6] p-6" data-testid="reg-bypass-pending-card">
                            <div className="flex items-start gap-3">
                                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#ffd700]/30 text-[#7a5b00]">
                                    <MailCheck className="h-5 w-5" />
                                </div>
                                <div className="flex-1">
                                    <h2 className="font-display text-xl font-bold text-[#0a1628]"> {tr40("Your application is under review")} </h2>
                                    <p className="mt-1 text-sm text-[#1a3a5c]/80">
                                        {coach?.display_name || 'Your coach'} {tr40("has your application and will get back to you shortly. We'll email you the moment a decision is made — you don't need to reapply.")} </p>
                                    <div className="mt-4">
                                        <Button asChild variant="outline" className="h-11 rounded-xl" data-testid="reg-bypass-pending-back">
                                            <Link to="/parent">{tr40("Back to my dashboard")}</Link>
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </Card>
                    )}

                    {/* Hide the wizard entirely if the parent has an active
                        approved/pending relationship — the bypass cards above
                        cover those cases. Otherwise render the classic apply
                        flow (new parents, declined re-applicants, dismissed
                        parents wanting a second chance, etc.). */}
                    {!(bypassReady || bypassPending) && (<>
                    {/* Coach hero — shown ONLY on the entry step (Account if
                        the parent isn't signed in, else Guardian) so the
                        parent immediately sees who they're applying to.
                        After step 1 the compact pill in the sticky header
                        keeps that context visible without taking up form
                        real estate. */}
                    {step === 0 && (
                        <div className="mb-5" data-testid="reg-coach-hero-wrap">
                            <CoachIntroHero coach={coach} />
                        </div>
                    )}
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={stepName}
                            initial={{ opacity: 0, x: 24 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -24 }}
                            transition={{ duration: 0.28, ease: [0.2, 0.8, 0.2, 1] }}
                        >
                            <Card className="sd-card p-5 sm:p-7">
                                {stepName === 'Account' && (
                                    <div>
                                        <h2 ref={titleRef} tabIndex={-1} className="font-display text-2xl font-bold text-[#0a1628] outline-none">{tr40("Create your account")}</h2>
                                        <p className="mt-1 text-sm text-[#1a3a5c]/80">{tr40("We use this to save your info, send confirmations, and store your waiver.")}</p>
                                        <div className="mt-4 rounded-xl border border-[#00d4ff]/30 bg-[#e6fbff]/60 px-4 py-3 text-sm text-[#0a1628]" data-testid="reg-account-existing-hint"> {tr40("Already have a SplashPad account from another coach?")}{' '}
                                            <Link
                                                to={`/login?from=${encodeURIComponent(`/coach/${slug}/register`)}`}
                                                className="font-semibold underline"
                                                data-testid="reg-account-login-link"
                                            > {tr40("Log in")} </Link>{' '} {tr40("and you can apply with just a couple of taps — one login works for every coach on SplashPad.")} </div>
                                        <div className="mt-5 space-y-4">
                                            <div>
                                                <Label>{tr40("Email")}</Label>
                                                <Input type="email" value={account.email} onChange={(e) => setAccount({ ...account, email: e.target.value })} className="mt-1 h-11 rounded-xl" data-testid="reg-account-email-input" />
                                            </div>
                                            <div>
                                                <Label>{tr40("Password")}</Label>
                                                <PasswordInput value={account.password} onChange={(e) => setAccount({ ...account, password: e.target.value })} className="mt-1 h-11 rounded-xl" data-testid="reg-account-password-input" testIdShow="reg-account-password-toggle" />
                                                <p className="mt-1 text-xs text-[#1a3a5c]/70">{tr40("If you already have an account, enter your existing password.")}</p>
                                            </div>
                                            {/* Legal acceptance is required for brand-new parents.
                                                We render it here on the Account step (which only shows
                                                when the parent isn't already logged in).

                                                Phase 44 (wingman audit fix): swapped bare native checkbox
                                                for shadcn Checkbox + bigger tappable row to match the
                                                COPPA consent improvement. */}
                                            <label
                                                htmlFor="reg-legal-checkbox"
                                                className="flex min-h-[56px] cursor-pointer items-start gap-3 rounded-xl border border-[#0a1628]/15 bg-[#f4f7fb] p-3 text-xs text-[#0a1628] transition-colors hover:bg-[#0a1628]/5 active:bg-[#0a1628]/10"
                                                data-testid="reg-legal-row"
                                            >
                                                <Checkbox
                                                    id="reg-legal-checkbox"
                                                    checked={agreedLegal}
                                                    onCheckedChange={(v) => setAgreedLegal(!!v)}
                                                    className="mt-0.5 h-5 w-5 shrink-0"
                                                    data-testid="reg-legal-checkbox"
                                                />
                                                <span className="leading-relaxed"> {tr40("I have read and agree to SplashPad's")}{' '}
                                                    <Link to="/privacy" target="_blank" className="font-semibold underline" data-testid="reg-privacy-link">{tr40("Privacy Policy")}</Link>
                                                    {' '}{tr40("and")}{' '}
                                                    <Link to="/terms" target="_blank" className="font-semibold underline" data-testid="reg-terms-link">{tr40("Terms of Service")}</Link>.
                                                    {legalVersion && <span className="ml-1 text-[#1a3a5c]/60">{t42("(v")}{legalVersion})</span>}
                                                </span>
                                            </label>
                                        </div>
                                    </div>
                                )}

                                {stepName === 'Guardian' && (
                                    <div>
                                        <h2 ref={titleRef} tabIndex={-1} className="font-display text-2xl font-bold text-[#0a1628] outline-none">{tr40("Guardian information")}</h2>
                                        <p className="mt-1 text-sm text-[#1a3a5c]/80">
                                            {isApplicationMode
                                                ? tr40("Tell us about yourself and where you live so the coach can decide if they can take on your family.")
                                                : tr40("Tell us a little about yourself so the coach can reach you.")}
                                        </p>
                                        <div className="mt-5 grid gap-4 sm:grid-cols-2">
                                            <div><Label>{tr40("First name")}</Label><Input value={guardian.first_name} onChange={(e) => setGuardian({ ...guardian, first_name: e.target.value })} className="mt-1 h-11 rounded-xl" data-testid="reg-guardian-first-input" /></div>
                                            <div><Label>{tr40("Last name")}</Label><Input value={guardian.last_name} onChange={(e) => setGuardian({ ...guardian, last_name: e.target.value })} className="mt-1 h-11 rounded-xl" data-testid="reg-guardian-last-input" /></div>
                                            <div>
                                                <Label>{tr40("Phone")} <span className="text-[#ff6b35]">*</span></Label>
                                                <Input
                                                    value={guardian.phone}
                                                    onChange={(e) => setGuardian({ ...guardian, phone: e.target.value })}
                                                    className="mt-1 h-11 rounded-xl"
                                                    data-testid="reg-guardian-phone-input"
                                                    placeholder="(555) 123-4567"
                                                    type="tel"
                                                    inputMode="tel"
                                                    autoComplete="tel"
                                                    aria-describedby="reg-sms-disclosure"
                                                />
                                            </div>
                                            <div className="hidden sm:block" />
                                            {/* === Phase A2P (Twilio 10DLC compliance) ===
                                                Mandatory small-font compliance disclosure directly below
                                                the phone field. Displayed to EVERY parent regardless of
                                                whether they opt into SMS below — Twilio's audit expects
                                                the disclosure copy to be visible before the opt-in
                                                checkbox so the parent knows what they'd be agreeing to. */}
                                            <div className="sm:col-span-2" data-testid="reg-sms-disclosure-block">
                                                <p
                                                    id="reg-sms-disclosure"
                                                    className="text-[11px] leading-relaxed text-[#1a3a5c]/70"
                                                    data-testid="reg-sms-disclosure"
                                                >
                                                    {SMS_DISCLOSURE_TEXT_PARENT}
                                                </p>
                                            </div>
                                            {/* SMS opt-in — TCR / 10DLC compliant. OPTIONAL, defaults OFF.
                                                Unlike the instructor Signup form (where SMS opt-in is
                                                MANDATORY for the coach's own account alerts), parent
                                                opt-in is voluntary — TCPA prohibits conditioning a service
                                                on marketing consent. The checkbox label is a short
                                                affirmative statement; the full compliance disclosure
                                                lives above (below the phone field). */}
                                            <div className="sm:col-span-2 rounded-xl border border-[#00d4ff]/40 bg-[#00d4ff]/5 p-4" data-testid="reg-sms-optin-card">
                                                <label className="flex items-start gap-3 cursor-pointer">
                                                    <Checkbox
                                                        checked={smsOptIn}
                                                        onCheckedChange={(v) => setSmsOptIn(!!v)}
                                                        className="mt-0.5"
                                                        data-testid="reg-sms-optin-checkbox"
                                                    />
                                                    <span className="text-sm text-[#1a3a5c] leading-relaxed">
                                                        <span className="font-semibold text-[#0a1628]">{SMS_CONSENT_LABEL_PARENT}</span>
                                                        <span className="mt-1 block text-[11px] text-[#1a3a5c]/70"> {tr40("Optional — you can reply")} <strong>{t42("STOP")}</strong> {tr40("to any text to opt out at any time. See")}{' '}
                                                            <Link to="/sms-terms" target="_blank" rel="noopener noreferrer" className="text-[#0a1628] underline hover:no-underline" data-testid="reg-sms-optin-terms-link"> {tr40("SMS Terms")} </Link>
                                                            {' · '}
                                                            <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="text-[#0a1628] underline hover:no-underline" data-testid="reg-sms-optin-privacy-link"> {tr40("Privacy Policy")} </Link>.
                                                        </span>
                                                    </span>
                                                </label>
                                            </div>
                                            <div className="sm:col-span-2 mt-2 rounded-xl border border-[#00d4ff]/30 bg-[#00d4ff]/5 p-4">
                                                <div className="text-xs font-semibold uppercase tracking-wider text-[#1a3a5c]">{tr40("Home address")} <span className="text-[#ff6b35]">{tr40("·required")}</span></div>
                                                <p className="mt-1 text-xs text-[#1a3a5c]/80">{tr40("Your coach uses this to confirm they can travel to your area or that the location works for you.")}</p>
                                                <div className="mt-3 grid gap-3 sm:grid-cols-6">
                                                    <div className="sm:col-span-6"><Label>{tr40("Street address")}</Label><Input value={guardian.address_street} onChange={(e) => setGuardian({ ...guardian, address_street: e.target.value })} className="mt-1 h-11 rounded-xl" data-testid="reg-guardian-street-input" placeholder={t42("123 Main St")} /></div>
                                                    <div className="sm:col-span-3"><Label>{tr40("City")}</Label><Input value={guardian.address_city} onChange={(e) => setGuardian({ ...guardian, address_city: e.target.value })} className="mt-1 h-11 rounded-xl" data-testid="reg-guardian-city-input" /></div>
                                                    <div className="sm:col-span-1"><Label>{tr40("State")}</Label><Input value={guardian.address_state} onChange={(e) => setGuardian({ ...guardian, address_state: e.target.value.toUpperCase() })} maxLength={2} className="mt-1 h-11 rounded-xl uppercase" data-testid="reg-guardian-state-input" placeholder={t42("CA")} /></div>
                                                    <div className="sm:col-span-2"><Label>{tr40("ZIP")}</Label><Input value={guardian.address_zip} onChange={(e) => setGuardian({ ...guardian, address_zip: e.target.value })} className="mt-1 h-11 rounded-xl" data-testid="reg-guardian-zip-input" placeholder="94110" /></div>
                                                </div>
                                            </div>
                                            <div><Label>{tr40("Emergency contact name")}</Label><Input value={guardian.emergency_contact_name} onChange={(e) => setGuardian({ ...guardian, emergency_contact_name: e.target.value })} className="mt-1 h-11 rounded-xl" /></div>
                                            <div><Label>{tr40("Emergency contact phone")}</Label><Input value={guardian.emergency_contact_phone} onChange={(e) => setGuardian({ ...guardian, emergency_contact_phone: e.target.value })} className="mt-1 h-11 rounded-xl" /></div>
                                            <div className="sm:col-span-2">
                                                <Label>{tr40("How did you hear about")} {coach.display_name}?</Label>
                                                <Input value={guardian.how_did_you_hear} onChange={(e) => setGuardian({ ...guardian, how_did_you_hear: e.target.value })} className="mt-1 h-11 rounded-xl" placeholder={tr40("Friend, Google, Instagram…")} />
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {stepName === 'Students' && (
                                    <div>
                                        <h2 ref={titleRef} tabIndex={-1} className="font-display text-2xl font-bold text-[#0a1628] outline-none">{tr40("Students")}</h2>
                                        <p className="mt-1 text-sm text-[#1a3a5c]/80">
                                            {myExistingKids.length > 0
                                                ? tr40("Pick which of your kids will be attending, or add a new one.")
                                                : tr40("Add the children who will be attending lessons.")}
                                        </p>

                                        {/* Phase 77a — Existing children picker.
                                            Rendered ONLY when the signed-in parent already has kids on
                                            file (Scott's UX ask: don't force re-typing name/DOB when
                                            applying to a new coach). Ticking a card injects a locked
                                            "Student N" card below with that child's info; unticking
                                            removes it. Emergency-med authorization is intentionally
                                            reset per application (never carried over) — the guardian
                                            must re-authorize for every new coach they apply to. */}
                                        {myExistingKids.length > 0 && (
                                            <div className="mt-5" data-testid="reg-existing-kids-picker">
                                                <div className="text-xs font-semibold uppercase tracking-wider text-[#1a3a5c]"> {tr40("Your children")} </div>
                                                <p className="mt-1 text-xs text-[#1a3a5c]/70"> {tr40("Tap to include them in this application. You can still edit medical or swim-level details below.")} </p>
                                                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                                                    {myExistingKids.map((kid) => {
                                                        const ticked = selectedExistingIds.has(kid.id);
                                                        const dobLabel = fmtKidDob(kid.dob);
                                                        return (
                                                            <label
                                                                key={kid.id}
                                                                htmlFor={`reg-existing-kid-${kid.id}`}
                                                                className={`flex cursor-pointer items-start gap-3 rounded-xl border-2 p-4 transition ${
                                                                    ticked
                                                                        ? 'border-[#00d4ff] bg-[#00d4ff]/5'
                                                                        : 'border-[#1a3a5c]/15 hover:border-[#1a3a5c]/30'
                                                                }`}
                                                                data-testid={`reg-existing-kid-row-${kid.id}`}
                                                            >
                                                                <Checkbox
                                                                    id={`reg-existing-kid-${kid.id}`}
                                                                    checked={ticked}
                                                                    onCheckedChange={(v) => toggleExistingKid(kid, !!v)}
                                                                    className="mt-0.5 h-5 w-5 shrink-0"
                                                                    data-testid={`reg-existing-kid-checkbox-${kid.id}`}
                                                                />
                                                                <div className="min-w-0 flex-1">
                                                                    <div className="truncate font-semibold text-[#0a1628]">
                                                                        {kid.first_name} {kid.last_name}
                                                                    </div>
                                                                    <div className="mt-0.5 text-xs text-[#1a3a5c]/70">
                                                                        {dobLabel ? `Born ${dobLabel}` : tr40("On your account")}
                                                                        {kid.swim_level ? ` · ${kid.swim_level}` : ''}
                                                                    </div>
                                                                </div>
                                                            </label>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}

                                        <div className="mt-5 space-y-5">
                                            {students.map((s, i) => {
                                                const fromExisting = !!s._from_existing_id;
                                                return (
                                                <Card
                                                    key={s._uid}
                                                    className={`sd-card p-5 ${fromExisting ? 'border-[#00d4ff]/30 bg-[#00d4ff]/[0.03]' : ''}`}
                                                    data-testid={`reg-student-card-${i}`}
                                                >
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center gap-2">
                                                            <div className="font-semibold text-[#0a1628]">{tr40("Student")} {i + 1}</div>
                                                            {fromExisting && (
                                                                <Badge
                                                                    variant="outline"
                                                                    className="border-[#00d4ff]/40 bg-[#00d4ff]/10 text-[10px] font-medium text-[#0a1628]"
                                                                    data-testid={`reg-student-existing-badge-${i}`}
                                                                > {tr40("From your account")} </Badge>
                                                            )}
                                                        </div>
                                                        {(fromExisting || students.length > 1) && (
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() => removeStudent(i)}
                                                                data-testid={`reg-student-remove-${i}`}
                                                                title={fromExisting ? tr40("Don't include this child") : tr40("Remove this student")}
                                                            >
                                                                <Trash2 className="h-4 w-4" />
                                                            </Button>
                                                        )}
                                                    </div>
                                                    <div className="mt-3 grid gap-3 sm:grid-cols-2">
                                                        {/* First / Last / DOB are locked when the row was
                                                            prefilled from an existing kid — parents update
                                                            those fields in their Parent → Students tab, not
                                                            during a new coach application, so we don't
                                                            create split-brain records. */}
                                                        <div>
                                                            <Label>{tr40("First name")}</Label>
                                                            <Input
                                                                value={s.first_name}
                                                                onChange={(e) => updateStudent(i, { first_name: e.target.value })}
                                                                readOnly={fromExisting}
                                                                disabled={fromExisting}
                                                                className={`mt-1 h-11 rounded-xl ${fromExisting ? 'bg-[#f4f7fb]' : ''}`}
                                                                data-testid={`reg-student-first-input-${i}`}
                                                            />
                                                        </div>
                                                        <div>
                                                            <Label>{tr40("Last name")}</Label>
                                                            <Input
                                                                value={s.last_name}
                                                                onChange={(e) => updateStudent(i, { last_name: e.target.value })}
                                                                readOnly={fromExisting}
                                                                disabled={fromExisting}
                                                                className={`mt-1 h-11 rounded-xl ${fromExisting ? 'bg-[#f4f7fb]' : ''}`}
                                                                data-testid={`reg-student-last-input-${i}`}
                                                            />
                                                        </div>
                                                        <div>
                                                            <Label>{tr40("Date of birth")}</Label>
                                                            {fromExisting ? (
                                                                <Input
                                                                    value={fmtKidDob(s.dob)}
                                                                    readOnly
                                                                    disabled
                                                                    className="mt-1 h-11 rounded-xl bg-[#f4f7fb]"
                                                                    data-testid={`reg-student-dob-input-${i}`}
                                                                />
                                                            ) : (
                                                                <DateOfBirthInput
                                                                    value={s.dob}
                                                                    onChange={(iso) => updateStudent(i, { dob: iso })}
                                                                    className="mt-1 h-11 rounded-xl"
                                                                    data-testid={`reg-student-dob-input-${i}`}
                                                                />
                                                            )}
                                                        </div>
                                                        <div><Label>{tr40("Gender (optional)")}</Label>
                                                            <Select value={s.gender || undefined} onValueChange={(v) => updateStudent(i, { gender: v })}>
                                                                <SelectTrigger className="mt-1 h-11 rounded-xl"><SelectValue placeholder={tr40("Choose…")} /></SelectTrigger>
                                                                <SelectContent>
                                                                    <SelectItem value="male">{tr40("Male")}</SelectItem>
                                                                    <SelectItem value="female">{tr40("Female")}</SelectItem>
                                                                    <SelectItem value="other">{tr40("Other")}</SelectItem>
                                                                </SelectContent>
                                                            </Select>
                                                        </div>
                                                        {settings?.swim_level_question !== false && (
                                                            <div className="sm:col-span-2"><Label>{tr40("Swim level")}</Label>
                                                                <Select value={s.swim_level || undefined} onValueChange={(v) => updateStudent(i, { swim_level: v })}>
                                                                    <SelectTrigger className="mt-1 h-11 rounded-xl"><SelectValue placeholder={tr40("Choose…")} /></SelectTrigger>
                                                                    <SelectContent>
                                                                        <SelectItem value="Non-swimmer">{tr40("Non-swimmer")}</SelectItem>
                                                                        <SelectItem value="Can float/kick">{tr40("Can float / kick")}</SelectItem>
                                                                        <SelectItem value="Knows basic strokes">{tr40("Knows basic strokes")}</SelectItem>
                                                                        <SelectItem value="Competitive swimmer">{tr40("Competitive swimmer")}</SelectItem>
                                                                    </SelectContent>
                                                                </Select>
                                                            </div>
                                                        )}
                                                        <div className="sm:col-span-2"><Label>{tr40("Medical/special needs (optional)")}</Label>
                                                            <Textarea value={s.medical_notes} onChange={(e) => updateStudent(i, { medical_notes: e.target.value })} className="mt-1 rounded-xl" rows={3} />
                                                        </div>
                                                        <div className="sm:col-span-2">
                                                            {/* 44px+ touch target — the parent label wraps both
                                                                the visible checkbox AND the description so the
                                                                ENTIRE row toggles, not just the (mobile-tiny)
                                                                native checkbox. shadcn Checkbox renders a 20px
                                                                control inside a 44px hit zone. */}
                                                            <label
                                                                htmlFor={`em-${i}`}
                                                                className="flex cursor-pointer items-start gap-3 rounded-xl border border-[#0a1628]/10 bg-[#f4f7fb] p-3 transition-colors hover:bg-[#0a1628]/5 active:bg-[#0a1628]/10"
                                                                data-testid={`reg-student-em-auth-row-${i}`}
                                                            >
                                                                <Checkbox
                                                                    id={`em-${i}`}
                                                                    checked={s.emergency_med_authorized}
                                                                    onCheckedChange={(v) => updateStudent(i, { emergency_med_authorized: !!v })}
                                                                    className="mt-0.5 h-5 w-5"
                                                                    data-testid={`reg-student-em-auth-${i}`}
                                                                />
                                                                <span className="text-sm font-normal leading-relaxed text-[#0a1628]"> {tr40("I authorize the coach to seek emergency medical treatment for my child if needed.")} </span>
                                                            </label>
                                                        </div>
                                                    </div>
                                                </Card>
                                                );
                                            })}
                                            {/* Empty state — signed-in parent hasn't picked or added
                                                any kid yet. Nudge them toward the picker above or the
                                                "Add another student" button below. */}
                                            {students.length === 0 && (
                                                <div
                                                    className="rounded-xl border-2 border-dashed border-[#1a3a5c]/20 bg-[#f4f7fb] p-5 text-center text-sm text-[#1a3a5c]/80"
                                                    data-testid="reg-students-empty-state"
                                                >
                                                    {myExistingKids.length > 0
                                                        ? tr40("Tick a child above to include them, or add a brand-new student below.")
                                                        : tr40("No students yet. Add one to continue.")}
                                                </div>
                                            )}
                                            {settings?.allow_multiple_students !== false && (
                                                <Button variant="outline" onClick={addStudent} className="rounded-xl" data-testid="reg-add-student-button">
                                                    <Plus className="mr-2 h-4 w-4" /> {myExistingKids.length > 0 ? tr40("Add a new student") : tr40("Add another student")}
                                                </Button>
                                            )}

                                            {/* COPPA — parent/guardian consent for processing minor data.
                                                Required on every application (not just first-time parents)
                                                because consent is per-child, not per-account.

                                                Phase 44 (wingman audit fix): replaced the bare <input type=
                                                "checkbox"> (~16px native, very hard to tap on mobile) with
                                                shadcn Checkbox inside a 44px+ tappable row. Coaches reported
                                                parents giving up because clicking the consent box "didn't
                                                light up the Continue button". */}
                                            <label
                                                htmlFor="reg-coppa-checkbox"
                                                className="mt-2 flex min-h-[60px] cursor-pointer items-start gap-3 rounded-xl border-2 border-[#00d4ff]/30 bg-[#00d4ff]/5 p-4 text-sm text-[#0a1628] transition-colors hover:bg-[#00d4ff]/10 active:bg-[#00d4ff]/15"
                                                data-testid="reg-coppa-row"
                                            >
                                                <Checkbox
                                                    id="reg-coppa-checkbox"
                                                    checked={guardianConsent}
                                                    onCheckedChange={(v) => setGuardianConsent(!!v)}
                                                    className="mt-0.5 h-5 w-5 shrink-0"
                                                    data-testid="reg-coppa-checkbox"
                                                />
                                                <span className="leading-relaxed">
                                                    <strong>{tr40("Parent / guardian consent.")}</strong> {tr40("I am the parent or legal guardian of the child(ren) listed above and consent to SplashPad processing their information for swim lesson delivery (waivers, scheduling, attendance, and communications with my coach).")} </span>
                                            </label>
                                        </div>
                                    </div>
                                )}

                                {stepName === 'Location' && (
                                    <div data-testid="reg-location-step">
                                        <h2 ref={titleRef} tabIndex={-1} className="font-display text-2xl font-bold text-[#0a1628] outline-none"> {tr40("Where would you like lessons?")} </h2>
                                        <p className="mt-1 text-sm text-[#1a3a5c]/80">
                                            {(coach?.service_mode || 'fixed') === 'both'
                                                ? tr40("Your coach offers both fixed-pool lessons and mobile (they come to you). Pick the option that works best.")
                                                : tr40("Your coach teaches at multiple locations. Pick the one you'd like to attend.")}
                                        </p>

                                        {(coach?.service_mode || 'fixed') === 'both' && (
                                            <div className="mt-5 grid gap-3 sm:grid-cols-3" data-testid="reg-delivery-pref-grid">
                                                {[
                                                    { id: 'fixed', label: 'Fixed location', blurb: 'I come to one of the coach\u2019s pools.' },
                                                    { id: 'mobile', label: 'Mobile', blurb: 'Coach travels to me.' },
                                                    { id: 'any', label: 'Either is fine', blurb: 'I\u2019m flexible \u2014 coach decides.' },
                                                ].map((opt) => {
                                                    const selected = deliveryPreference === opt.id;
                                                    return (
                                                        <button
                                                            key={opt.id}
                                                            type="button"
                                                            onClick={() => {
                                                                setDeliveryPreference(opt.id);
                                                                if (opt.id === 'mobile') setPreferredLocationId('');
                                                            }}
                                                            className={`rounded-2xl border-2 p-4 text-left transition ${selected ? 'border-[#00d4ff] bg-[#00d4ff]/5' : 'border-[#1a3a5c]/15 hover:border-[#1a3a5c]/30'}`}
                                                            data-testid={`reg-delivery-pref-${opt.id}`}
                                                        >
                                                            <div className="font-semibold text-[#0a1628]">{opt.label}</div>
                                                            <div className="mt-1 text-xs text-[#1a3a5c]/70">{opt.blurb}</div>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        )}

                                        {/* Location picker — shown for 'fixed' coaches with 2+ locations,
                                            and for 'both' coaches when the parent picks fixed/any. */}
                                        {(((coach?.service_mode || 'fixed') === 'fixed' && (coach?.locations || []).length > 1)
                                            || (coach?.service_mode === 'both' && deliveryPreference && deliveryPreference !== 'mobile')) && (
                                                <div className="mt-5">
                                                    <Label className="text-sm font-medium text-[#0a1628]">{tr40("Pick a location")}</Label>
                                                    <div className="mt-2 grid gap-3 sm:grid-cols-2" data-testid="reg-location-grid">
                                                        {(coach?.locations || []).map((loc) => {
                                                            const selected = preferredLocationId === loc.id;
                                                            return (
                                                                <button
                                                                    key={loc.id}
                                                                    type="button"
                                                                    onClick={() => setPreferredLocationId(loc.id)}
                                                                    className={`rounded-2xl border-2 p-4 text-left transition ${selected ? 'border-[#00d4ff] bg-[#00d4ff]/5' : 'border-[#1a3a5c]/15 hover:border-[#1a3a5c]/30'}`}
                                                                    data-testid={`reg-location-option-${loc.id}`}
                                                                >
                                                                    <div className="font-semibold text-[#0a1628]">{loc.name}</div>
                                                                    <div className="mt-1 text-xs text-[#1a3a5c]/80">{loc.address}</div>
                                                                    {loc.notes && <div className="mt-1 text-xs text-[#1a3a5c]/60">{loc.notes}</div>}
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            )}

                                        <p className="mt-4 text-xs text-[#1a3a5c]/70"> {tr40("You can change this from your parent dashboard later if your schedule shifts.")} </p>
                                    </div>
                                )}

                                {stepName === 'Package' && (
                                    <div>
                                        <h2 ref={titleRef} tabIndex={-1} className="font-display text-2xl font-bold text-[#0a1628] outline-none">{tr40("Choose a package")}</h2>
                                        <p className="mt-1 text-sm text-[#1a3a5c]/80">{tr40("Pick the package that’s right for your child.")}</p>
                                        <div className="mt-5 grid gap-4 sm:grid-cols-2">
                                            {packages.map((p) => {
                                                const selected = packageId === p.id;
                                                return (
                                                    <button key={p.id} type="button" onClick={() => setPackageId(p.id)}
                                                        className={`text-left rounded-2xl border p-5 transition-shadow duration-200 ${selected ? 'border-[#00d4ff] ring-2 ring-[#00d4ff]/30 shadow-[0_10px_25px_rgba(10,22,40,0.08)] bg-white' : 'border-[#1a3a5c]/15 bg-white hover:shadow-[0_6px_14px_rgba(10,22,40,0.06)]'}`}
                                                        data-testid={`reg-package-card-${p.id}`}
                                                    >
                                                        <div className="flex items-center justify-between">
                                                            <h3 className="font-display text-lg font-bold text-[#0a1628]">{p.name}</h3>
                                                            <div className="flex items-center gap-1">
                                                                {p.package_type === 'subscription' && <Badge className="bg-[#1a3a5c] text-white hover:bg-[#1a3a5c] text-[10px]">{tr40("Subscription")}</Badge>}
                                                                {p.is_popular && <Badge className="bg-[#ffd700] text-[#0a1628] hover:bg-[#ffd700]">{tr40("Popular")}</Badge>}
                                                            </div>
                                                        </div>
                                                        <div className="mt-1 text-2xl font-bold text-[#0a1628]">
                                                            {fmtPrice(p.price_cents, p?.currency)}
                                                            {p.package_type === 'subscription' && <span className="text-xs font-normal text-[#1a3a5c]/70"> / {p.billing_interval}</span>}
                                                        </div>
                                                        <div className="text-xs text-[#1a3a5c]/80">{p.session_count} {tr40("sessions ·")} {p.duration_minutes} {t42("min")}</div>
                                                        {/* Phase 80 — flexible ("sell now, schedule later") packages. */}
                                                        {p.flexible_schedule && (
                                                            <div className="mt-1 inline-flex items-center gap-1 rounded-full bg-[#00d4ff]/10 px-2 py-0.5 text-[11px] font-semibold text-[#0a1628]" data-testid={`reg-flexible-note-${p.id}`}> {tr40("Flexible schedule — your coach sets lesson times with you")} </div>
                                                        )}
                                                        <p className="mt-2 text-sm text-[#1a3a5c]">{p.description}</p>
                                                        <div className="mt-3">
                                                            {p.payment_method === 'manual_invoice' && <Badge variant="outline" className="border-[#1a3a5c]/20 text-[#1a3a5c]">{tr40("Coach will invoice")}</Badge>}
                                                            {p.payment_method === 'external_link' && <PaymentProviderBadge provider={p.payment_link_provider} />}
                                                        </div>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                {stepName === 'Schedule' && (
                                    <div>
                                        <h2 ref={titleRef} tabIndex={-1} className="font-display text-2xl font-bold text-[#0a1628] outline-none">{tr40("Pick a preferred slot")}</h2>
                                        <p className="mt-1 text-sm text-[#1a3a5c]/80">{tr40("Choose a time that works for your first lesson. Your remaining lessons will be scheduled with your coach.")}</p>
                                        <div className="mt-5">
                                            <Label>{tr40("Preferred date & time")}</Label>
                                            <Input type="datetime-local" value={preferredSlot} onChange={(e) => setPreferredSlot(e.target.value)} className="mt-1 h-11 rounded-xl" data-testid="reg-schedule-input" />
                                            <p className="mt-2 text-xs text-[#1a3a5c]/70">{tr40("If your coach hasn’t set availability yet, they’ll reach out to confirm.")}</p>
                                        </div>
                                    </div>
                                )}

                                {stepName === 'ClassTime' && selectedPkg && (
                                    <div data-testid="reg-class-time-step">
                                        <h2 ref={titleRef} tabIndex={-1} className="font-display text-2xl font-bold text-[#0a1628] outline-none">{tr40("Pick your class time")}</h2>
                                        <p className="mt-1 text-sm text-[#1a3a5c]/80">
                                            <strong>{selectedPkg.name}</strong> {tr40("runs at set times. Pick the one that works for your family — your coach can move you later if your schedule changes.")} </p>
                                        {/* Phase 0cj+ (Scott / Preschool camp feedback,
                                            2026-07) — re-show the coach's own description
                                            on the class-time step so parents have all the
                                            "what is this package" context while they're
                                            picking a slot. Only renders when the coach
                                            actually wrote one. */}
                                        {selectedPkg.description && selectedPkg.description.trim() && (
                                            <div
                                                className="mt-3 rounded-xl border border-[#00d4ff]/25 bg-[#00d4ff]/6 p-3 text-sm text-[#1a3a5c] whitespace-pre-wrap"
                                                data-testid="reg-class-time-pkg-description"
                                            >
                                                {selectedPkg.description.trim()}
                                            </div>
                                        )}
                                        <div className="mt-5 grid gap-3 sm:grid-cols-2">
                                            {(selectedPkg.class_slots || []).map((slot) => {
                                                const cap = slotCapacity[slot.id]?.capacity ?? (slot.capacity || selectedPkg.max_enrollment_slots || 0);
                                                const taken = slotCapacity[slot.id]?.taken ?? 0;
                                                const left = cap > 0 ? Math.max(0, cap - taken) : null;
                                                const isFull = cap > 0 && taken >= cap;
                                                const selected = classSlotId === slot.id;
                                                // Phase 44.9 — render 12h AM/PM (never military time).
                                                const slotTime = fmtTime12hLong(slot.time_local);
                                                // Phase 0cj+ (Scott / Preschool camp feedback,
                                                // 2026-07) — the auto-generated
                                                // "Every Monday at 5:00 PM · 4 lessons" line
                                                // was dropped per coach directive. The ONLY
                                                // description parents should see is the one
                                                // the coach wrote on the package itself
                                                // (rendered further up on the "Choose a package"
                                                // step via selectedPkg.description). Here we
                                                // only show the FACTUAL time + duration so the
                                                // parent still knows when to arrive.
                                                return (
                                                    <button
                                                        key={slot.id}
                                                        type="button"
                                                        disabled={isFull}
                                                        onClick={() => !isFull && setClassSlotId(slot.id)}
                                                        className={`text-left rounded-2xl border-2 p-4 transition ${selected ? 'border-[#00d4ff] bg-[#00d4ff]/5 shadow-[0_8px_22px_rgba(0,212,255,0.15)]' : isFull ? 'border-[#1a3a5c]/10 bg-[#f4f7fb] opacity-60 cursor-not-allowed' : 'border-[#1a3a5c]/15 bg-white hover:border-[#00d4ff]/50'}`}
                                                        data-testid={`reg-class-slot-${slot.id}`}
                                                    >
                                                        <div className="flex items-start justify-between gap-2">
                                                            <div>
                                                                <div className="font-display text-lg font-semibold text-[#0a1628]">
                                                                    {slot.label || (
                                                                        selectedPkg?.billing_type === 'one_time'
                                                                            ? 'Package'
                                                                            : (slot.type === 'weekly' ? 'Weekly class' : 'Class session')
                                                                    )}
                                                                </div>
                                                                {/* Phase 0cj+ (Scott / Preschool camp
                                                                    feedback, 2026-07) — factual time +
                                                                    duration only. Any explanatory text
                                                                    lives on the coach-written package
                                                                    description above this step. */}
                                                                <div className="text-[11px] text-[#1a3a5c]/70">
                                                                    {slot.type === 'weekly' ? slotTime : `${slot.date} at ${slotTime}`} · {slot.duration_minutes} {t42("min")}{slot.timezone ? ` · ${slot.timezone.replace('_', ' ')}` : ''}
                                                                </div>
                                                            </div>
                                                            <Badge className={isFull ? 'bg-[#ff6b35]/15 text-[#ff6b35]' : 'bg-[#00d4ff]/15 text-[#0a1628] hover:bg-[#00d4ff]/15'}>
                                                                {cap > 0 ? (isFull ? tr40("Full") : `${left} of ${cap} left`) : tr40("Open")}
                                                            </Badge>
                                                        </div>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                        {!classSlotId && (
                                            <p className="mt-3 text-xs text-[#1a3a5c]/70">{tr40("Pick a class time to continue. You can change it later from your dashboard.")}</p>
                                        )}
                                    </div>
                                )}

                                {stepName === 'SingleLessonTime' && selectedPkg && (
                                    <div data-testid="reg-single-lesson-time-step">
                                        <h2 ref={titleRef} tabIndex={-1} className="font-display text-2xl font-bold text-[#0a1628] outline-none">{tr40("Pick your lesson date & time")}</h2>
                                        <p className="mt-1 text-sm text-[#1a3a5c]/80">
                                            <strong>{selectedPkg.name}</strong> {tr40("is a single private lesson. Pick the date and time that works for you — your coach will see this on their schedule the moment you check out.")} </p>
                                        <div className="mt-5 max-w-md">
                                            <label
                                                htmlFor="reg-single-lesson-at"
                                                className="block text-sm font-semibold text-[#0a1628]"
                                            > {tr40("Lesson date & time")} </label>
                                            <input
                                                id="reg-single-lesson-at"
                                                type="datetime-local"
                                                value={singleLessonAt}
                                                onChange={(e) => setSingleLessonAt(e.target.value)}
                                                min={new Date(Date.now() + 60 * 60 * 1000).toISOString().slice(0, 16)}
                                                className="mt-2 w-full rounded-xl border-2 border-[#1a3a5c]/15 bg-white px-4 py-3 text-base text-[#0a1628] outline-none transition focus:border-[#00d4ff] focus:ring-2 focus:ring-[#00d4ff]/20"
                                                data-testid="reg-single-lesson-at-input"
                                            />
                                            <p className="mt-2 text-xs text-[#1a3a5c]/70"> {tr40("Times are in your coach's local time zone")} {coach?.time_zone ? ` (${coach.time_zone.replace(/_/g, ' ')})` : ''}{tr40(". Need a different time after you check out? Message your coach — they can reschedule in one click.")} </p>
                                        </div>
                                    </div>
                                )}

                                {stepName === 'Waiver' && (
                                    <div>
                                        <h2 ref={titleRef} tabIndex={-1} className="font-display text-2xl font-bold text-[#0a1628] outline-none">{tr40("Waiver & cancellation policy")}</h2>
                                        <p className="mt-1 text-sm text-[#1a3a5c]/80">{tr40("Agree to your coach's waiver and cancellation policy, then type your name to sign.")}</p>
                                        <p className="mt-1 text-xs text-[#1a3a5c]/70" data-testid="reg-waiver-reassurance"> {tr40("This is the standard participation waiver you'd sign at any swim school — it's between you and")} {coach?.display_name || 'your coach'}{tr40(", and you'll get a PDF copy for your records. Tap either document to read it in full.")} </p>

                                        {/* Owner decision (2026-09-11): documents open on tap; one checkbox covers both. */}
                                        <div className="mt-5">
                                            <WaiverAgreement
                                                coachName={coach?.display_name}
                                                waiverText={coach.waiver_text}
                                                cancellation={{ general: coach.cancellation_general_text, weather: coach.cancellation_weather_text, sick: coach.cancellation_sick_text }}
                                                agreed={waiver.agreed && waiver.cancellation_agreed}
                                                onAgreedChange={(v) => setWaiver({ ...waiver, agreed: v, cancellation_agreed: v })}
                                                testIdPrefix="reg"
                                            />
                                        </div>

                                        {/* === Signature (covers both) ======================== */}
                                        <div className="mt-6 rounded-xl border-2 border-dashed border-[#0a1628]/15 bg-[#f4f7fb] p-4">
                                            <p className="text-xs uppercase tracking-wider text-[#1a3a5c]/70">{tr40("Your signature covers both documents")}</p>
                                            <div className="mt-2 grid gap-3 sm:grid-cols-2">
                                                <div>
                                                    <Label htmlFor="waiver-signed-name">{tr40("Type your full legal name to sign")}</Label>
                                                    <Input id="waiver-signed-name" value={waiver.signed_name} onChange={(e) => setWaiver({ ...waiver, signed_name: e.target.value })} className="mt-1 h-11 rounded-xl bg-white" placeholder={t42("Jane Doe")} data-testid="waiver-typed-signature-input" />
                                                </div>
                                                <div>
                                                    <Label>{tr40("Date")}</Label>
                                                    <Input value={new Date().toLocaleDateString()} disabled className="mt-1 h-11 rounded-xl bg-white" />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {stepName === 'Fee' && (
                                    <div>
                                        <h2 ref={titleRef} tabIndex={-1} className="font-display text-2xl font-bold text-[#0a1628] outline-none"> {tr40("One last step")} </h2>
                                        <p className="mt-1 text-sm text-[#1a3a5c]/80">
                                            {coach?.display_name || 'Your coach'} {tr40("requires a fee with new applications. Your card is saved securely now — nothing is charged until you're approved.")} </p>
                                        {!feeResult && (
                                            <div className="mt-5" data-testid="reg-application-fee-step">
                                                <ApplicationFeeStep
                                                    coachSlug={slug}
                                                    coachDisplayName={coach?.display_name || 'your coach'}
                                                    parent={{
                                                        email: account.email || user?.email || '',
                                                        first_name: guardian.first_name,
                                                        last_name: guardian.last_name,
                                                        phone: guardian.phone,
                                                    }}
                                                    studentCount={students.length || 1}
                                                    publishableKey={stripePublishableKey}
                                                    onSuccess={(res) => {
                                                        setFeeResult(res);
                                                        if (res?.fee_discount_code) setFeeCode(res.fee_discount_code);
                                                    }}
                                                    onSkip={() => setFeeSkipped(true)}
                                                    onWaived={(res) => {
                                                        // Phase 98 — 100% fee code: no card needed, step disappears.
                                                        setFeeCode(res?.code_applied || '');
                                                        setFeeSkipped(true);
                                                        toast.success(`Code ${res?.code_applied || ''} applied — ${(res?.label || 'registration fee').toLowerCase()} waived!`);
                                                    }}
                                                    payAnotherWay={payAnotherWay}
                                                    onExternal={(info) => {
                                                        // Phase 99 — fee owed offline; no card saved.
                                                        setFeeResult({ external: true, ...info });
                                                        toast.success(tr40("Got it — you’ll pay the fee directly. Your coach will confirm once it arrives."));
                                                    }}
                                                />
                                            </div>
                                        )}
                                        {feeResult && (
                                            <Card className="sd-card mt-5 p-5" data-testid="reg-application-fee-confirmed">
                                                <div className="flex items-start gap-3">
                                                    <div className="mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#00d4ff]/15">
                                                        <Check className="h-5 w-5 text-[#00d4ff]" />
                                                    </div>
                                                    <div className="flex-1">
                                                        <div className="font-semibold text-[#0a1628]">
                                                            {feeResult.external ? tr40("Paying another way") : tr40("Card saved")}
                                                        </div>
                                                        <div className="text-sm text-[#1a3a5c]/80">
                                                            {feeResult.external
                                                                ? `You'll pay the ${(feeResult.label || 'fee').toLowerCase()} directly (Venmo, Zelle, or cash). Your coach confirms once it arrives. Tap Submit application to finish.`
                                                                : tr40("You'll only be charged when your application is approved. Tap Submit application to finish.")}
                                                        </div>
                                                    </div>
                                                </div>
                                            </Card>
                                        )}
                                    </div>
                                )}

                                {stepName === 'Payment' && (
                                    <div>
                                        <h2 ref={titleRef} tabIndex={-1} className="font-display text-2xl font-bold text-[#0a1628] outline-none">
                                            {usesConnectCheckout ? tr40("Payment") : tr40("Review & payment")}
                                        </h2>
                                        <p className="mt-1 text-sm text-[#1a3a5c]/80">
                                            {usesConnectCheckout
                                                ? tr40("Your card is charged securely by Stripe. We never see your card number.")
                                                : tr40("Please review your selection. Payment instructions will appear after you confirm.")}
                                        </p>

                                        {/* ===== NEW FLOW: Stripe Connect Elements checkout ===== */}
                                        {usesConnectCheckout && !paymentResult && (
                                            <div className="mt-5" data-testid="reg-stripe-payment-step">
                                                <PaymentStep
                                                    coachSlug={slug}
                                                    coachDisplayName={coach?.display_name || 'your coach'}
                                                    pkg={selectedPkg}
                                                    parent={{
                                                        email: account.email || user?.email || '',
                                                        first_name: guardian.first_name,
                                                        last_name: guardian.last_name,
                                                        phone: guardian.phone,
                                                    }}
                                                    publishableKey={stripePublishableKey}
                                                    onSuccess={(res) => {
                                                        // res = { intent_type, intent_id, payment_method_id, save_card }
                                                        setPaymentResult(res);
                                                    }}
                                                    onCancel={() => {
                                                        // Parent abandoned the card form — let them go back a step.
                                                        setStep((s) => Math.max(0, s - 1));
                                                    }}
                                                    payAnotherWay={
                                                        // Phase 99 — never offered for class packages.
                                                        (selectedPkg?.class_slots || []).length === 0 ? payAnotherWay : { enabled: false }
                                                    }
                                                    onExternal={(info) => {
                                                        setPaymentResult({ external: true, ...info });
                                                        toast.success(tr40("Got it — you’ll pay your coach directly. Your spot stays pending until they confirm."));
                                                    }}
                                                />
                                            </div>
                                        )}

                                        {/* Once PaymentStep confirms, show a success card and let
                                            them tap Continue to fire the /register submission. */}
                                        {usesConnectCheckout && paymentResult && (
                                            <Card className="sd-card mt-5 p-5" data-testid="reg-stripe-payment-confirmed">
                                                <div className="flex items-start gap-3">
                                                    <div className="mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#00d4ff]/15">
                                                        <Check className="h-5 w-5 text-[#00d4ff]" />
                                                    </div>
                                                    <div className="flex-1">
                                                        <div className="font-semibold text-[#0a1628]">
                                                            {paymentResult.external
                                                                ? tr40("Paying another way")
                                                                : paymentResult.intent_type === 'payment_intent' ? tr40("Payment received") : tr40("Card saved")}
                                                        </div>
                                                        <div className="text-sm text-[#1a3a5c]/80">
                                                            {paymentResult.external
                                                                ? `You'll pay ${fmtPrice(selectedPkg?.price_cents || 0, selectedPkg?.currency)} directly (Venmo, Zelle, or cash). Your spot stays pending until your coach confirms. Tap Continue to finish.`
                                                                : paymentResult.intent_type === 'payment_intent'
                                                                    ? `${fmtPrice(selectedPkg?.price_cents || 0, selectedPkg?.currency)} charged. Tap Continue to finish enrollment.`
                                                                    : tr40("Your card is on file. Tap Continue to finish enrollment.")}
                                                        </div>
                                                    </div>
                                                </div>
                                            </Card>
                                        )}

                                        {/* ===== LEGACY FLOW: external link / manual invoice ===== */}
                                        {!usesConnectCheckout && (
                                            <Card className="sd-card mt-5 p-5">
                                                <div className="flex items-center justify-between">
                                                    <div>
                                                        <div className="flex items-center gap-2">
                                                            <span className="font-semibold text-[#0a1628]">{selectedPkg?.name}</span>
                                                            {selectedPkg?.package_type === 'subscription' && <Badge className="bg-[#1a3a5c] text-white hover:bg-[#1a3a5c]">{tr40("Subscription")}</Badge>}
                                                        </div>
                                                        <div className="text-xs text-[#1a3a5c]/80">
                                                            {selectedPkg?.session_count} {tr40("sessions ·")} {selectedPkg?.duration_minutes} {t42("min")} {selectedPkg?.package_type === 'subscription' && <span> {tr40("· billed every")} {selectedPkg.billing_interval}</span>}
                                                        </div>
                                                    </div>
                                                    <div className="text-right">
                                                        <div className="text-2xl font-bold text-[#0a1628]">{fmtPrice(selectedPkg?.price_cents || 0, selectedPkg?.currency)}</div>
                                                        {selectedPkg?.package_type === 'subscription' && <div className="text-xs text-[#1a3a5c]/70">/ {selectedPkg.billing_interval}</div>}
                                                    </div>
                                                </div>
                                                <div className="mt-3">
                                                    {selectedPkg?.payment_method === 'manual_invoice' && (
                                                        <div className="rounded-xl bg-[#fff7e6] p-3 text-sm text-[#0a1628]"><Sparkles className="mr-1 inline h-4 w-4" /> {coach.display_name} {tr40("will contact you within 24 hours with payment details.")}</div>
                                                    )}
                                                    {selectedPkg?.payment_method === 'external_link' && (
                                                        <div className="rounded-xl bg-[#e6f9ff] p-3 text-sm text-[#0a1628]"><PaymentProviderBadge provider={selectedPkg.payment_link_provider} /> {tr40("You’ll complete payment with the coach’s preferred provider after confirmation.")}</div>
                                                    )}
                                                </div>
                                            </Card>
                                        )}
                                    </div>
                                )}

                                {stepName === 'Confirmation' && (
                                    <div className="text-center">
                                        {!result && submitting && !submitError && <div className="py-10" data-testid="reg-submitting-state">{tr40("Submitting your application…")}</div>}

                                        {/* === Recoverable submission-error UI ===================
                                            When the auto-submit fails (e.g., backend 422 validation
                                            or "account exists"), surface a clear error block WITH
                                            a Back button so the parent can fix the offending step
                                            instead of being stranded on the spinner. */}
                                        {!result && submitError && (
                                            <div className="py-2 text-left" data-testid="reg-submit-error-state">
                                                <div className="mx-auto mb-3 inline-flex h-14 w-14 items-center justify-center rounded-full bg-[#ff6b35]/15">
                                                    <ArrowLeft className="h-7 w-7 text-[#ff6b35]" />
                                                </div>
                                                <h2 ref={titleRef} tabIndex={-1} className="font-display text-2xl font-bold text-[#0a1628] outline-none text-center" data-testid="reg-submit-error-title">
                                                    {submitError.title}
                                                </h2>
                                                <p className="mt-2 text-center text-sm text-[#1a3a5c]" data-testid="reg-submit-error-message">
                                                    {submitError.message}
                                                </p>
                                                {submitError.issues && submitError.issues.length > 0 && (
                                                    <ul className="mx-auto mt-4 max-w-md list-disc rounded-xl border border-[#ff6b35]/30 bg-[#fff6f0] p-4 pl-9 text-sm text-[#0a1628]" data-testid="reg-submit-error-issues">
                                                        {submitError.issues.map((iss) => (
                                                            <li key={iss.field || iss.label} data-testid={`reg-submit-error-issue-${iss.field}`}>
                                                                <strong>{iss.label}</strong> — {iss.msg}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                )}
                                                <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                                                    <Button
                                                        variant="outline"
                                                        onClick={() => {
                                                            const targetStep = submitError.backStep && visibleSteps.includes(submitError.backStep)
                                                                ? visibleSteps.indexOf(submitError.backStep)
                                                                : Math.max(0, visibleSteps.length - 2); // step before Confirmation
                                                            setSubmitError(null);
                                                            setStep(targetStep);
                                                        }}
                                                        className="h-11 rounded-xl border-2 border-[#0a1628]/15 text-[#0a1628] hover:bg-[#0a1628]/5"
                                                        data-testid="reg-submit-error-back-button"
                                                    >
                                                        <ArrowLeft className="mr-1 h-4 w-4" /> {tr40("Go back & fix")} </Button>
                                                    <Button
                                                        onClick={() => { setSubmitError(null); submit(); }}
                                                        className="sd-cta sd-focus h-11 rounded-xl"
                                                        data-testid="reg-submit-error-retry-button"
                                                    > {tr40("Try submitting again")} </Button>
                                                </div>
                                            </div>
                                        )}
                                        {result && isApplicationMode && (
                                            <div className="py-2">
                                                <div className="mx-auto mb-3 inline-flex h-14 w-14 items-center justify-center rounded-full bg-[#00d4ff]/15">
                                                    <Check className="h-7 w-7 text-[#00d4ff]" />
                                                </div>
                                                {/* Phase 76d — success copy branches on whether the coach
                                                    auto-approved us (Instant Signup) vs the classic
                                                    apply-and-wait flow. Prior copy told every parent to
                                                    wait 24-48h even when they were already approved,
                                                    which was confusing enough that families abandoned
                                                    without ever picking a class. */}
                                                <h2 ref={titleRef} tabIndex={-1} className="font-display text-2xl font-bold text-[#0a1628] outline-none">
                                                    {result?.application?.status === 'approved' ? tr40("You're all set — pick a class next! 🏊") : tr40("Application submitted! 🏊")}
                                                </h2>
                                                <p className="mt-2 text-sm text-[#1a3a5c]">
                                                    {result?.application?.status === 'approved' ? (
                                                        <>{tr40("Thanks for signing up with")} <strong>{coach.display_name}</strong>{tr40(". You're approved instantly — no waiting. Pick a package to book your first class.")}</>
                                                    ) : (
                                                        <>{tr40("Thanks for applying with")} <strong>{coach.display_name}</strong>{tr40(". We've sent your information to your coach for review.")}</>
                                                    )}
                                                </p>

                                                <Card className="sd-card mt-5 p-5 text-left">
                                                    <div className="font-semibold text-[#0a1628]">{tr40("What happens next")}</div>
                                                    {result?.application?.status === 'approved' ? (
                                                        <ol className="mt-3 list-decimal pl-5 text-sm text-[#1a3a5c] space-y-2">
                                                            <li>{tr40("Head back to your coach's page and pick the class or package you want.")}</li>
                                                            <li>{tr40("Complete payment (secured by Stripe) — you're enrolled the moment it clears.")}</li>
                                                            <li>{tr40("You'll get a confirmation email with lesson details.")}</li>
                                                        </ol>
                                                    ) : (
                                                        <ol className="mt-3 list-decimal pl-5 text-sm text-[#1a3a5c] space-y-2">
                                                            <li>{tr40("Your coach reviews your application (usually within 24-48 hours)")}</li>
                                                            <li>{tr40("They may call or text you to schedule an assessment")}</li>
                                                            <li>{tr40("Once approved, you'll get an email and can purchase lesson packages from your account")}</li>
                                                        </ol>
                                                    )}
                                                    <div className="mt-4 rounded-xl bg-[#fff7e6] p-3 text-xs text-[#0a1628]">
                                                        <Sparkles className="mr-1 inline h-3.5 w-3.5" /> {tr40("We've also sent you a confirmation email at")} <strong>{result.user?.email}</strong>.
                                                    </div>
                                                </Card>

                                                {/* Phase 99 — offline registration fee reminder. */}
                                                {result.fee_external && (
                                                    <Card className="sd-card mt-4 border-[#ffd700]/50 bg-[#fffbeb] p-5 text-left" data-testid="reg-fee-external-card">
                                                        <div className="flex items-start gap-3">
                                                            <HandCoins className="mt-0.5 h-5 w-5 shrink-0 text-[#0a1628]" />
                                                            <div>
                                                                <div className="font-semibold text-[#0a1628]">{tr40("Don't forget your registration fee")}</div>
                                                                <div className="mt-0.5 text-xs text-[#1a3a5c]/80"> {tr40("You chose to pay")} <strong>{coach.display_name}</strong> {tr40("directly. It stays pending until they confirm the payment arrived.")} </div>
                                                                {result.pay_another_way_instructions && (
                                                                    <div className="mt-2 whitespace-pre-wrap rounded-lg bg-white/70 p-2.5 text-sm text-[#1a3a5c]" data-testid="reg-fee-external-instructions">
                                                                        {result.pay_another_way_instructions}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </Card>
                                                )}

                                                <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                                                    {result?.application?.status === 'approved' ? (
                                                        <Button asChild className="sd-cta sd-focus h-11 rounded-xl" data-testid="reg-pick-class-button"><Link to={`/coach/${slug}`}>{tr40("Pick a class")}</Link></Button>
                                                    ) : (
                                                        <Button asChild className="sd-cta sd-focus h-11 rounded-xl" data-testid="reg-go-account-button"><Link to="/parent/dashboard">{tr40("Go to my account")}</Link></Button>
                                                    )}
                                                    <Button asChild variant="outline" className="h-11 rounded-xl"><Link to={`/coach/${slug}`}>{tr40("Back to coach's page")}</Link></Button>
                                                </div>
                                            </div>
                                        )}
                                        {result && !isApplicationMode && (
                                            <div className="py-2">
                                                <div className="mx-auto mb-3 inline-flex h-14 w-14 items-center justify-center rounded-full bg-[#00d4ff]/15">
                                                    <Check className="h-7 w-7 text-[#00d4ff]" />
                                                </div>
                                                <h2 ref={titleRef} tabIndex={-1} className="font-display text-2xl font-bold text-[#0a1628] outline-none">{tr40("You're registered!")}</h2>
                                                <p className="mt-1 text-sm text-[#1a3a5c]">{tr40("Welcome to")} {coach.display_name}.</p>
                                                <Card className="sd-card mt-5 p-5 text-left">
                                                    <div className="font-semibold text-[#0a1628]">{result.package?.name}</div>
                                                    <div className="text-sm text-[#1a3a5c]/80">{result.package?.session_count} {tr40("sessions ·")} {result.package?.duration_minutes} {t42("min ·")} {fmtPrice(result.package?.price_cents, result.package?.currency)}</div>
                                                </Card>

                                                {/* Phase 80 — flexible package confirmation. No times were
                                                    picked at checkout, so set the expectation clearly:
                                                    the coach schedules directly with the family. */}
                                                {result.package?.flexible_schedule && (
                                                    <Card className="sd-card mt-4 p-5 text-left" data-testid="reg-flexible-confirmation-card">
                                                        <div className="flex items-center gap-3">
                                                            <CalendarDays className="h-5 w-5 text-[#00d4ff]" />
                                                            <div>
                                                                <div className="font-semibold text-[#0a1628]">{tr40("Your coach will schedule with you")}</div>
                                                                <div className="mt-0.5 text-xs text-[#1a3a5c]/80"> {tr40("This package doesn't have fixed lesson times —")} <strong>{coach.display_name}</strong> {tr40("will reach out to set your lesson days and times, if they haven't already. You can track your remaining lessons any time from your parent dashboard.")} </div>
                                                            </div>
                                                        </div>
                                                    </Card>
                                                )}

                                                {/* Phase 99 — offline payment confirmation. Spot is PENDING
                                                    until the coach confirms the money arrived. */}
                                                {result.external_payment && (
                                                    <Card className="sd-card mt-4 border-[#ffd700]/50 bg-[#fffbeb] p-5 text-left" data-testid="reg-external-payment-card">
                                                        <div className="flex items-start gap-3">
                                                            <HandCoins className="mt-0.5 h-5 w-5 shrink-0 text-[#0a1628]" />
                                                            <div>
                                                                <div className="font-semibold text-[#0a1628]">{tr40("One more step — send your payment")}</div>
                                                                <div className="mt-0.5 text-xs text-[#1a3a5c]/80"> {tr40("You chose to pay")} <strong>{coach.display_name}</strong> {tr40("directly. Your spot stays")} <strong>{tr40("pending")}</strong> {tr40("until they confirm your payment arrived — you'll get a confirmation the moment they do.")} </div>
                                                                {result.pay_another_way_instructions && (
                                                                    <div className="mt-2 whitespace-pre-wrap rounded-lg bg-white/70 p-2.5 text-sm text-[#1a3a5c]" data-testid="reg-external-payment-instructions">
                                                                        {result.pay_another_way_instructions}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </Card>
                                                )}

                                                {/* Phase 68b — pay-later confirmation card. Shown
                                                    only when the backend response indicates the
                                                    coach picked "Enroll immediately, collect
                                                    payment later" on this package. Reassures the
                                                    parent that they're enrolled AND surfaces an
                                                    optional "Pay now" button if a Stripe Payment
                                                    Link was auto-generated. */}
                                                {result.pay_later && (
                                                    <Card className="sd-card mt-4 p-5 text-left" data-testid="reg-pay-later-card">
                                                        <div className="flex items-center gap-3">
                                                            <MailCheck className="h-5 w-5 text-[#00d4ff]" />
                                                            <div>
                                                                <div className="font-semibold text-[#0a1628]">{tr40("You're all set — invoice on the way")}</div>
                                                                <div className="mt-0.5 text-xs text-[#1a3a5c]/80"> {tr40("Your spot is reserved.")} <strong>{coach.display_name}</strong> {tr40("will send you an invoice — you can pay any time from your Payments tab.")} </div>
                                                            </div>
                                                        </div>
                                                        {result.payment_link_url && (
                                                            <Button asChild variant="outline" className="mt-4 w-full h-11 rounded-xl" data-testid="reg-pay-later-optional-pay-button">
                                                                <a href={result.payment_link_url} target="_blank" rel="noreferrer"> {tr40("Pay now (optional)")} <ExternalLink className="ml-1 h-4 w-4" />
                                                                </a>
                                                            </Button>
                                                        )}
                                                    </Card>
                                                )}

                                                {result.payment_method === 'external_link' && result.payment_link_url && !result.pay_later && (
                                                    <Card className="sd-card mt-4 p-5 text-left">
                                                        <div className="flex items-center justify-between">
                                                            <div>
                                                                <div className="font-semibold text-[#0a1628]">{tr40("Complete your payment")}</div>
                                                                <div className="text-xs text-[#1a3a5c]/80">{tr40("Pay using the coach's preferred provider.")}</div>
                                                            </div>
                                                            <PaymentProviderBadge provider={data?.packages.find(p => p.id === packageId)?.payment_link_provider || 'other'} />
                                                        </div>
                                                        {result.payment_instructions && (
                                                            <div className="mt-3 rounded-xl bg-[#f4f7fb] p-3 text-sm text-[#1a3a5c]">{result.payment_instructions}</div>
                                                        )}
                                                        <Button asChild className="sd-cta sd-focus mt-4 w-full h-11 rounded-xl" data-testid="reg-complete-payment-button">
                                                            <a href={result.payment_link_url} target="_blank" rel="noreferrer"> {tr40("Complete Payment")} <ExternalLink className="ml-1 h-4 w-4" />
                                                            </a>
                                                        </Button>
                                                        <Button variant="outline" onClick={async () => {
                                                            try {
                                                                await api.post(`/public/parent/confirm-payment/${result.enrollment.id}`);
                                                                toast.success(tr40("Thanks! The coach will confirm shortly."));
                                                            } catch { toast.error(tr40("Could not record. Please try again.")); }
                                                        }} className="mt-2 w-full rounded-xl" data-testid="reg-claim-paid-button"> {tr40("I've completed payment")} </Button>
                                                    </Card>
                                                )}
                                                {result.payment_method === 'manual_invoice' && (
                                                    <Card className="sd-card mt-4 p-5 text-left">
                                                        <div className="flex items-center gap-3">
                                                            <MailCheck className="h-5 w-5 text-[#00d4ff]" />
                                                            <div>
                                                                <div className="font-semibold text-[#0a1628]">{tr40("Awaiting invoice")}</div>
                                                                <div className="text-xs text-[#1a3a5c]/80">{coach.display_name} {tr40("will contact you within 24 hours with payment details.")}</div>
                                                            </div>
                                                        </div>
                                                    </Card>
                                                )}

                                                <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                                                    <Button asChild className="sd-cta sd-focus h-11 rounded-xl" data-testid="reg-go-account-button"><Link to="/parent/dashboard">{tr40("Go to my account")}</Link></Button>
                                                    <Button asChild variant="outline" className="h-11 rounded-xl"><Link to={`/coach/${slug}`}>{tr40("Back to coach's page")}</Link></Button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </Card>
                        </motion.div>
                    </AnimatePresence>
                    {/* === Phase A2P (Twilio 10DLC compliance) ===
                        Terms of Service + Privacy Policy links plainly
                        visible at the bottom of the registration card on
                        every step. Twilio audits reject signup forms where
                        the legal links are only inside a checkbox row — a
                        standalone footer makes them unmissable. */}
                    <div
                        className="mx-auto mt-6 max-w-3xl px-4 pb-4 text-center text-xs text-[#1a3a5c]/80 sm:px-6"
                        data-testid="reg-legal-footer"
                    >
                        <Link
                            to="/terms"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-semibold text-[#0a1628] underline hover:no-underline"
                            data-testid="reg-terms-footer-link"
                        > {tr40("Terms of Service")} </Link>
                        <span className="mx-2 text-[#1a3a5c]/40" aria-hidden="true">·</span>
                        <Link
                            to="/privacy"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-semibold text-[#0a1628] underline hover:no-underline"
                            data-testid="reg-privacy-footer-link"
                        > {tr40("Privacy Policy")} </Link>
                        <span className="mx-2 text-[#1a3a5c]/40" aria-hidden="true">·</span>
                        <Link
                            to="/sms-terms"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-semibold text-[#0a1628] underline hover:no-underline"
                            data-testid="reg-sms-terms-footer-link"
                        > {tr40("SMS Terms")} </Link>
                    </div>
                    </>)}
                </div>
            </main>

            {/* Sticky bottom action bar */}
            {!(bypassReady || bypassPending) && stepName !== 'Confirmation' && (
                <div className="sticky bottom-0 z-30 sd-glass border-t pb-[env(safe-area-inset-bottom)]" data-testid="reg-action-bar">
                    <div className="mx-auto max-w-3xl px-4 py-3 pb-20 sm:px-6 sm:pb-3 flex items-center justify-between gap-2">
                        <Button
                            variant="outline"
                            onClick={prev}
                            disabled={step === 0}
                            className="h-11 rounded-xl border-2 border-[#0a1628]/15 text-[#0a1628] hover:bg-[#0a1628]/5 disabled:opacity-40"
                            data-testid="registration-back-button"
                        >
                            <ArrowLeft className="mr-1 h-4 w-4" /> {tr40("Back")} </Button>
                        {stepName === 'Fee' || stepName === 'Payment' || (stepName === 'Waiver' && !visibleSteps.includes('Payment') && !visibleSteps.includes('Fee')) || (stepName === 'Schedule' && !visibleSteps.includes('Waiver') && !visibleSteps.includes('Payment')) ? (
                            <Button onClick={() => { next(); }} className="sd-cta sd-focus h-11 rounded-xl" data-testid="registration-next-button">
                                {stepName === 'Payment'
                                    ? tr40("Confirm registration")
                                    : stepName === 'Fee'
                                        ? tr40("Submit application")
                                        : (isApplicationMode ? tr40("Submit application") : tr40("Continue"))}
                                <ArrowRight className="ml-1 h-4 w-4" />
                            </Button>
                        ) : (
                            <Button onClick={next} className="sd-cta sd-focus h-11 rounded-xl" data-testid="registration-next-button"> {tr40("Continue")} <ArrowRight className="ml-1 h-4 w-4" />
                            </Button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
